import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type FormEvent } from "react";
import {
  ArrowClockwise,
  CaretDown,
  ChartBar,
  Check,
  ClockCounterClockwise,
  DownloadSimple,
  DotsThree,
  FileArrowUp,
  Folders,
  IdentificationCard,
  MagnifyingGlass,
  Money,
  Package,
  PencilSimple,
  Plus,
  Printer,
  Receipt,
  SignOut,
  Stack,
  Tag,
  UploadSimple,
  UserCircle,
  UsersThree,
  X,
} from "@phosphor-icons/react";
import {
  formatCny,
  effectiveProductCategory,
  getProductCategoryPrefixes,
  matchesCategoryPrefix,
  getStaffPermissions,
  getDealerPermissions,
  hasStaffPermission,
  paymentStatus,
  parseYuanToCents,
  todayInShanghai,
  type FulfillmentStatus,
  type CodeCategoryNode,
  type SessionUser,
  type StaffPermission,
} from "@longjie/shared";
import { ApiError, download, request, saveBlob } from "./api";
import type {
  Category,
  Customer,
  DashboardData,
  Order,
  Product,
  Sku,
  StaffAccount,
} from "./types";
import { AccountCredentialsModal } from "./AccountCredentialsModal";
import { CustomerEditorModal } from "./CustomerEditorModal";
import { OwnPasswordModal } from "./OwnPasswordModal";
import { DealerPermissionPicker, DealerPermissionsModal, StaffPermissionPicker, StaffPermissionsModal } from "./AccountPermissions";
import { PermissionPresets } from "./PermissionPresets";
import { InventoryAdmin } from "./InventoryAdmin";
import { FontSizeControl } from "./FontSizeControl";
import { MobileHeaderControls } from "./MobileHeaderControls";
import { MobileSkuCard } from "./MobileSkuCard";
import { MobileCategoryPicker } from "./MobileCategoryPicker";
import { usePhoneLayout } from "./usePhoneLayout";
import { MobileDisclosure } from "./MobileDisclosure";
import { orderDatePresets, orderDateRange, type OrderDatePreset } from "./order-date-range";
import { PlaceOrderAdmin } from "./PlaceOrderAdmin";
import { OrderPrintPreview } from "./OrderPrint";
import "./order-operations.css";
import { ImportsAdmin } from "./ImportsAdmin";
import { PromotionEditor, PromotionsAdmin } from "./PromotionsAdmin";
import { CategoriesAdmin, CodeCategoryEditor, ProductCategoryEditor } from "./CategoriesAdmin";
import { CodeCategorySelect, ProductCodeCategoryNote } from "./CodeCategoryBrowser";
import { ProductCategoryFilter } from "./ProductCategoryFilter";
import { DealerCatalogLayout } from "./DealerCatalogLayout";
import { useSynchronizedCodeCategories } from "./code-category-sync";
import { ProductImageEditor } from "./ProductImageEditor";
import { ProductImage } from "./ProductImage";
import { ProductSearch } from "./ProductSearch";
import { useProductSearch } from "./useProductSearch";
import { useCatalogRefresh } from "./useCatalogRefresh";
import { productSearchPath } from "./catalog-selection";
import {
  BrandMark,
  Button,
  EmptyState,
  Field,
  LoadError,
  Modal,
  Money as MoneyValue,
  StatusBadge,
  Toast,
} from "./components";
import {
  formatDate,
  formatDateTime,
  fulfillmentLabel,
  paymentLabel,
} from "./ui-utils";

type AdminTab =
  | "dashboard"
  | "orders"
  | "place-order"
  | "inventory"
  | "customers"
  | "staff"
  | "products"
  | "categories"
  | "promotions"
  | "imports"
  | "audit";

const baseNavItems: Array<{ id: AdminTab; label: string; icon: typeof ChartBar }> =
  [
    { id: "dashboard", label: "经营概览", icon: ChartBar },
    { id: "orders", label: "订单台账", icon: Receipt },
    { id: "place-order", label: "代客下单", icon: Plus },
    { id: "products", label: "商品目录", icon: Package },
    { id: "categories", label: "分类管理", icon: Folders },
    { id: "promotions", label: "限时促销", icon: Tag },
    { id: "imports", label: "批量导入", icon: FileArrowUp },
    { id: "customers", label: "客户账号", icon: UsersThree },
    { id: "staff", label: "内部账号", icon: IdentificationCard },
    { id: "audit", label: "操作日志", icon: ClockCounterClockwise },
  ];

const mobileNavigationQuery = "(max-width: 980px)";

function subscribeToMobileNavigation(update: () => void) {
  if (typeof window.matchMedia !== "function") return () => undefined;
  const media = window.matchMedia(mobileNavigationQuery);
  media.addEventListener("change", update);
  return () => media.removeEventListener("change", update);
}

function mobileNavigationSnapshot() {
  return typeof window.matchMedia === "function"
    && window.matchMedia(mobileNavigationQuery).matches;
}

function useMobileNavigation() {
  return useSyncExternalStore(
    subscribeToMobileNavigation,
    mobileNavigationSnapshot,
    () => false,
  );
}

function loadErrorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback;
}

export function AdminApp({
  user,
  inventoryEnabled = false,
  onLogout,
}: {
  user: SessionUser;
  inventoryEnabled?: boolean;
  onLogout: () => void;
}) {
  const isPhone = usePhoneLayout();
  const isAdmin = user.staffRole === "ADMIN";
  const can = (permission: StaffPermission) => hasStaffPermission(user, permission);
  const navPermissions: Record<AdminTab, StaffPermission> = {
    dashboard: "dashboard.read", orders: "orders.read", "place-order": "orders.create",
    inventory: "inventory.read", products: "products.read", categories: "categories.read",
    promotions: "promotions.read", imports: "imports.read", customers: "customers.read",
    staff: "staff.read", audit: "audit.read",
  };
  const allNavItems = inventoryEnabled
    ? [
        ...baseNavItems.slice(0, 2),
        { id: "inventory" as const, label: "成品仓库", icon: Stack },
        ...baseNavItems.slice(2),
      ]
    : baseNavItems;
  const navItems = allNavItems.filter((item) => can(navPermissions[item.id]) &&
    !(item.id === "categories" && inventoryEnabled && can("inventory.read")));
  const phoneNavIds: AdminTab[] = ["dashboard", "orders", "place-order", "inventory", "products", "customers", "staff", "promotions", "imports", "audit", "categories"];
  const phoneNavItems = phoneNavIds.flatMap((id) => navItems.filter((item) => item.id === id));
  const phoneNavRows = Math.ceil(phoneNavItems.length / 5);
  const phoneNavColumns = phoneNavRows ? Math.ceil(phoneNavItems.length / phoneNavRows) : 1;
  const mobilePrimaryNavItems = isPhone ? phoneNavItems : navItems.filter((item) =>
    ["dashboard", "orders", "customers", "products"].includes(item.id),
  );
  const mobileMoreNavItems = navItems.filter(
    (item) =>
      !mobilePrimaryNavItems.some((primaryItem) => primaryItem.id === item.id),
  );
  const [selectedTab, setTab] = useState<AdminTab>("dashboard");
  const [ordersRefreshVersion, setOrdersRefreshVersion] = useState(0);
  const [importFocus, setImportFocus] = useState<"purchases" | undefined>();
  const tab = navItems.some((item) => item.id === selectedTab) ? selectedTab : navItems[0]?.id;
  const [menuOpen, setMenuOpen] = useState(false);
  const [ownPasswordOpen, setOwnPasswordOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [orderPending, setOrderPending] = useState(false);
  const [recentlyCreatedOrder, setRecentlyCreatedOrder] = useState<{ id: string; orderNo: string } | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    tone: "success" | "error" | "info";
  } | null>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const morePanelRef = useRef<HTMLElement>(null);
  const isMobile = useMobileNavigation();
  const mobileMenuOpen = isMobile && !isPhone && menuOpen;
  const notify = (
    message: string,
    tone: "success" | "error" | "info" = "success",
  ) => setToast({ message, tone });
  const openTab = (nextTab: AdminTab) => {
    setRecentlyCreatedOrder(null);
    setImportFocus(undefined);
    if (nextTab === "orders") setOrdersRefreshVersion((version) => version + 1);
    setTab(nextTab);
    setMenuOpen(false);
  };

  useEffect(() => {
    if (isPhone) window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [isPhone, tab]);

  async function logout() {
    if (orderPending) return;
    await request("/api/v1/auth/logout", { method: "POST" });
    onLogout();
  }

  useEffect(() => {
    if (!orderPending) return;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [orderPending]);

  useEffect(() => {
    const panel = morePanelRef.current;
    if (!mobileMenuOpen || !panel) return;
    const activePanel = panel;
    const moreButton = moreButtonRef.current;

    const selector = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusableElements = () => [...activePanel.querySelectorAll<HTMLElement>(selector)]
      .filter((element) => !element.hidden && element.getAttribute("aria-hidden") !== "true");
    const frame = window.requestAnimationFrame(() => {
      (
        activePanel.querySelector<HTMLElement>('[aria-current="page"]')
        ?? activePanel.querySelector<HTMLElement>("nav button")
        ?? focusableElements()[0]
        ?? activePanel
      ).focus();
    });

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setMenuOpen(false);
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = focusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        activePanel.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable.at(-1)!;
      if (event.shiftKey && (document.activeElement === first || document.activeElement === activePanel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handleKeyDown);
      if (moreButton?.isConnected) moreButton.focus();
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const bodyOverflow = document.body.style.overflow;
    const htmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = htmlOverflow;
    };
  }, [mobileMenuOpen]);

  const current = navItems.find((item) => item.id === tab);
  const roleLabel = isAdmin ? "管理员" : user.staffRole === "CUSTOM" ? "自定义权限" : "查看员";
  const moreIsCurrent = mobileMoreNavItems.some((item) => item.id === tab);
  return (
    <div className="admin-shell" style={isPhone ? {
      "--staff-nav-rows": phoneNavRows,
      "--staff-nav-columns": phoneNavColumns,
    } as CSSProperties : undefined}>
      <aside
        id="admin-navigation"
        className="admin-sidebar"
        aria-hidden={isMobile || undefined}
      >
        <div className="admin-sidebar__brand">
          <BrandMark />
          <span>内部后台</span>
        </div>
        <nav aria-label="后台主导航">
          <small>工作台</small>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={tab === item.id ? "active" : ""}
                aria-current={tab === item.id ? "page" : undefined}
                disabled={orderPending}
                onClick={() => openTab(item.id)}
              >
                <Icon weight={tab === item.id ? "fill" : "regular"} />
                {item.label}
              </button>
            );
          })}
        </nav>
        <FontSizeControl />
        <Button variant="ghost" disabled={orderPending} onClick={() => setOwnPasswordOpen(true)}>修改我的密码</Button>
        <div className="admin-sidebar__account">
          <UserCircle weight="duotone" />
          <span>
            <b>{user.displayName}</b>
            <small>{roleLabel}</small>
          </span>
          <button onClick={() => void logout()} aria-label="退出登录" disabled={orderPending}>
            <SignOut />
          </button>
        </div>
      </aside>

      <div
        className="admin-workspace"
        aria-hidden={mobileMenuOpen || undefined}
        inert={mobileMenuOpen ? true : undefined}
      >
        <header className="admin-topbar">
          {isPhone ? <div className="staff-mobile-heading">
            <BrandMark />
            <h1>{current?.label ?? "账号权限"}</h1>
          </div> : <div>
            <small>阳江龙杰日丰订货系统 / {current?.label ?? "账号权限"}</small>
            <h1>{current?.label ?? "账号权限"}</h1>
          </div>}
          {isPhone ? <MobileHeaderControls disabled={orderPending} onAccount={() => setAccountOpen(true)} />
            : !isAdmin && <StatusBadge tone="info">{roleLabel === "查看员" ? "只读权限" : roleLabel}</StatusBadge>}
        </header>
        <main className="admin-main">
          {tab === "dashboard" && (
            <Dashboard onGoOrders={can("orders.read") ? () => openTab("orders") : undefined} />
          )}
          {tab === "orders" && (
            <OrdersAdmin
              canConfigurePrint={isAdmin}
              key={recentlyCreatedOrder?.id ?? "all-orders"}
              isAdmin={can("orders.write")}
              canCreateOrders={can("orders.create")}
              canManagePayments={can("payments.write")}
              canExportOrders={can("orders.export")}
              canReadCustomers={can("customers.read")}
              inventoryEnabled={inventoryEnabled}
              refreshVersion={ordersRefreshVersion}
              notify={notify}
              focusOrder={recentlyCreatedOrder}
              onPendingChange={setOrderPending}
              onViewAllOrders={() => setRecentlyCreatedOrder(null)}
              onPlaceOrder={() => {
                setRecentlyCreatedOrder(null);
                setTab("place-order");
              }}
            />
          )}
          {tab === "place-order" && can("orders.create") && (
            <PlaceOrderAdmin
              notify={notify}
              onPendingChange={setOrderPending}
              onCreated={(order) => {
                setOrderPending(false);
                notify(`代客下单成功：${order.orderNo} · ${formatCny(order.totalCents)}`);
                setRecentlyCreatedOrder({ id: order.id, orderNo: order.orderNo });
                setTab("orders");
              }}
            />
          )}
          {tab === "inventory" && inventoryEnabled && (
            <InventoryAdmin isAdmin={can("inventory.write")} canManageCategories={can("categories.write")} canManageProducts={can("products.write")} notify={notify}
              onPurchaseImport={can("imports.read") ? () => { setImportFocus("purchases"); setTab("imports"); setMenuOpen(false); } : undefined} />
          )}
          {tab === "customers" && (
            <CustomersAdmin isAdmin={can("customers.write")} canConfigurePermissions={isAdmin} notify={notify} />
          )}
          {tab === "staff" && (
            <StaffAdmin
              currentUserId={user.id}
              isAdmin={isAdmin}
              notify={notify}
            />
          )}
          {tab === "products" && (
            <ProductsAdmin isAdmin={can("products.write")} canManageCategories={can("categories.write")} canManagePromotions={can("promotions.write")} notify={notify} />
          )}
          {tab === "categories" && (
            <CategoriesAdmin isAdmin={can("categories.write")} canManageProducts={can("products.write")} notify={notify} />
          )}
          {tab === "promotions" && (
            <PromotionsAdmin isAdmin={can("promotions.write")} notify={notify} />
          )}
          {tab === "imports" && (
            <ImportsAdmin key={importFocus ?? "all"} initialType={importFocus} isAdmin={can("imports.write")} allowedTypes={([
              ["customers", "customers.write"], ["products", "products.write"], ["purchases", "inventory.write"],
            ] as const).filter(([type, permission]) => can(permission) && (type !== "products" || can("categories.write"))).map(([type]) => type)} inventoryEnabled={inventoryEnabled} notify={notify} />
          )}
          {tab === "audit" && <AuditAdmin />}
          {!tab && <EmptyState title="暂未开放业务内容" description="请联系管理员勾选此账号可见的功能。你仍可退出登录。" />}
        </main>
      </div>
      {isMobile && (!isPhone || phoneNavItems.length > 0) && (
        <nav
          className="admin-mobile-nav"
          aria-label="手机端主导航"
          aria-hidden={mobileMenuOpen || undefined}
          inert={mobileMenuOpen ? true : undefined}
        >
          {mobilePrimaryNavItems.map((item) => {
            const Icon = item.icon;
            const active = tab === item.id;
            return (
              <button
                key={item.id}
                className={active ? "active" : ""}
                aria-current={active ? "page" : undefined}
                disabled={orderPending}
                onClick={() => openTab(item.id)}
              >
                <Icon weight={active ? "fill" : "regular"} />
                <span>{item.label}</span>
              </button>
            );
          })}
          {!isPhone && <button
            ref={moreButtonRef}
            className={moreIsCurrent || mobileMenuOpen ? "active" : ""}
            aria-expanded={mobileMenuOpen}
            aria-controls="admin-mobile-more"
            disabled={orderPending}
            onClick={() => setMenuOpen(true)}
          >
            <DotsThree weight={moreIsCurrent || mobileMenuOpen ? "fill" : "bold"} />
            <span>更多</span>
          </button>}
        </nav>
      )}
      {mobileMenuOpen && (
        <>
          <button
            className="admin-mobile-more__scrim"
            onClick={() => setMenuOpen(false)}
            aria-label="关闭更多功能"
          />
          <section
            ref={morePanelRef}
            id="admin-mobile-more"
            className="admin-mobile-more"
            role="dialog"
            aria-modal="true"
            aria-label="更多功能"
            tabIndex={-1}
          >
            <header>
              <div>
                <h2>更多功能</h2>
                <p>账号管理、数据维护与操作记录</p>
              </div>
              <button className="icon-button" onClick={() => setMenuOpen(false)} aria-label="关闭更多功能">
                <X />
              </button>
            </header>
            <nav aria-label="更多功能导航">
              {mobileMoreNavItems.map((item) => {
                const Icon = item.icon;
                const active = tab === item.id;
                return (
                  <button
                    key={item.id}
                    className={active ? "active" : ""}
                    aria-current={active ? "page" : undefined}
                    disabled={orderPending}
                    onClick={() => openTab(item.id)}
                  >
                    <Icon weight={active ? "fill" : "regular"} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>
            {!isPhone && <><FontSizeControl />
            <Button variant="secondary" disabled={orderPending} onClick={() => { setMenuOpen(false); setOwnPasswordOpen(true); }}>修改我的密码</Button></>}
            <div className="admin-mobile-more__account">
              <UserCircle weight="duotone" />
              <span>
                <b>{user.displayName}</b>
                <small>{roleLabel} · 账号设置</small>
              </span>
              <button className="icon-button" onClick={() => void logout()} aria-label="退出登录" disabled={orderPending}>
                <SignOut />
              </button>
            </div>
          </section>
        </>
      )}
      {isPhone && accountOpen && <Modal title="账号设置" onClose={() => setAccountOpen(false)}>
        <div className="dealer-account-settings">
          <div><h3>{user.displayName}</h3><p>{roleLabel} · 内部人员</p></div>
          <FontSizeControl />
          <Button variant="secondary" disabled={orderPending} onClick={() => { setAccountOpen(false); setOwnPasswordOpen(true); }}>修改我的密码</Button>
          <Button variant="ghost" disabled={orderPending} onClick={() => void logout()}><SignOut />退出登录</Button>
        </div>
      </Modal>}
      {ownPasswordOpen && <OwnPasswordModal onClose={() => setOwnPasswordOpen(false)} onSaved={() => {
        setOwnPasswordOpen(false);
        notify("我的密码已更新");
        window.dispatchEvent(new Event("account-credentials-changed"));
      }} />}
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}

function Dashboard({ onGoOrders }: { onGoOrders: (() => void) | undefined }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  useEffect(() => {
    let active = true;
    void request<DashboardData>("/api/v1/admin/dashboard")
      .then((result) => {
        if (active) setData(result);
      })
      .catch((caught: unknown) => {
        if (active) setError(loadErrorMessage(caught, "经营数据加载失败"));
      });
    return () => {
      active = false;
    };
  }, [retryKey]);
  if (error) return <LoadError title="经营概览暂时无法加载" description={`${error}。请检查网络连接后重试。`} onRetry={() => { setData(null); setError(""); setRetryKey((value) => value + 1); }} />;
  if (!data) return <LoadingRows />;
  const metrics = [
    {
      label: "今日新订单",
      value: `${data.stats.todayOrderCount} 单`,
      meta: "按上海时区统计",
      tone: "blue",
    },
    {
      label: "进行中订单金额",
      value: formatCny(data.stats.activeOrderTotalCents),
      meta: `${data.stats.activeOrderCount} 张未完结订单`,
      tone: "steel",
    },
    {
      label: "未收余额",
      value: formatCny(data.stats.outstandingCents),
      meta: "有效订单减去已登记收款",
      tone: "orange",
    },
  ];
  return (
    <>
      <section className="admin-intro dashboard-intro">
        <div>
          <p className="eyebrow">BUSINESS LEDGER</p>
          <h2>订单和回款，一眼看清</h2>
          <p>统计客户订货及内部代客订单，查看出货和收款进度。</p>
        </div>
        {onGoOrders && <Button onClick={onGoOrders}>查看订单台账</Button>}
      </section>
      <section className="metric-rail">
        {metrics.map((metric, index) => (
          <article
            key={metric.label}
            className={`metric metric--${metric.tone}`}
          >
            <span>0{index + 1}</span>
            <small>{metric.label}</small>
            <strong>{metric.value}</strong>
            <p>{metric.meta}</p>
          </article>
        ))}
      </section>
      {onGoOrders && <section className="admin-section dashboard-orders">
        <div className="section-heading">
          <div>
            <h2>最近订单</h2>
            <p>按下单时间从新到旧</p>
          </div>
        </div>
        <div className="data-table compact">
          <div className="data-table__head">
            <span>订单号</span>
            <span>客户</span>
            <span>下单时间</span>
            <span>金额</span>
            <span>状态</span>
          </div>
          {data.recentOrders.map((order) => (
            <div className="data-table__row" key={order.id}>
              <span data-label="订单号"><b>{order.orderNo}</b></span>
              <span data-label="客户"><span>{order.customerName}</span></span>
              <span data-label="下单时间"><span>{formatDateTime(order.createdAt)}</span></span>
              <span data-label="金额"><MoneyValue cents={order.totalCents} /></span>
              <span data-label="状态">
                <span className="order-statuses">
                  {order.status === "CANCELLED" ? (
                    <StatusBadge tone="danger">已取消</StatusBadge>
                  ) : (
                    <StatusBadge tone={fulfillmentLabel(order.fulfillmentStatus, order.deliveryMethod).tone}>
                      {fulfillmentLabel(order.fulfillmentStatus, order.deliveryMethod).label}
                    </StatusBadge>
                  )}
                  <StatusBadge
                    tone={paymentLabel(paymentStatus(order.totalCents, order.paidCents)).tone}
                  >
                    {paymentLabel(paymentStatus(order.totalCents, order.paidCents)).label}
                  </StatusBadge>
                </span>
              </span>
            </div>
          ))}
        </div>
      </section>}
    </>
  );
}

function OrdersAdmin({ isAdmin, canConfigurePrint, notify, onPlaceOrder, focusOrder, onViewAllOrders, onPendingChange, canCreateOrders, canManagePayments, canExportOrders, canReadCustomers, inventoryEnabled, refreshVersion }: AdminSectionProps & {
  canConfigurePrint: boolean;
  canCreateOrders: boolean;
  canManagePayments: boolean;
  canExportOrders: boolean;
  canReadCustomers: boolean;
  inventoryEnabled: boolean;
  refreshVersion: number;
  onPlaceOrder: () => void;
  focusOrder: { id: string; orderNo: string } | null;
  onViewAllOrders: () => void;
  onPendingChange: (pending: boolean) => void;
}) {
  const initialQuery = focusOrder?.orderNo ?? "";
  const focusedOrderId = focusOrder?.id;
  const [orders, setOrders] = useState<Order[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filters, setFilters] = useState({
    query: initialQuery,
    customerId: "",
    status: "",
    fulfillmentStatus: "",
    paymentStatus: "",
    dateFrom: "",
    dateTo: "",
  });
  const [openId, setOpenId] = useState<string | null>(focusOrder?.id ?? null);
  const [printOrder, setPrintOrder] = useState<Order | null>(null);
  const [paymentOrder, setPaymentOrder] = useState<Order | null>(null);
  const [shipmentOrder, setShipmentOrder] = useState<Order | null>(null);
  const [cancelOrder, setCancelOrder] = useState<Order | null>(null);
  const [reverseTarget, setReverseTarget] = useState<{
    order: Order;
    paymentId: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshError, setRefreshError] = useState("");
  const [advancingId, setAdvancingId] = useState<string | null>(null);
  const appliedParams = useRef(new URLSearchParams(initialQuery ? { query: initialQuery } : {}).toString());
  const cachedCustomers = useRef<Customer[] | null>(null);
  const activeRequest = useRef<AbortController | null>(null);
  const requestVersion = useRef(0);
  const hasLoaded = useRef(false);

  const refreshOrders = useCallback(async (background = false) => {
    // Focus and visibility events often arrive together. Keep one refresh in flight.
    if (background && activeRequest.current) return;
    activeRequest.current?.abort();
    const controller = new AbortController();
    activeRequest.current = controller;
    const version = ++requestVersion.current;
    if (!background) {
      setLoading(true);
      setError("");
      setRefreshError("");
    }
    try {
      const [orderResult, customerResult] = await Promise.all([
        request<{ orders: Order[] }>(`/api/v1/admin/orders?${appliedParams.current}`, {
          signal: controller.signal,
          cache: "no-store",
        }),
        !canReadCustomers || cachedCustomers.current !== null
          ? Promise.resolve({ customers: canReadCustomers ? cachedCustomers.current! : [] })
          : request<{ customers: Customer[] }>("/api/v1/admin/customers", { signal: controller.signal }),
      ]);
      if (version !== requestVersion.current || controller.signal.aborted) return;
      setOrders(focusedOrderId ? orderResult.orders.filter((order) => order.id === focusedOrderId) : orderResult.orders);
      cachedCustomers.current = canReadCustomers ? customerResult.customers : null;
      setCustomers(customerResult.customers);
      hasLoaded.current = true;
      setError("");
      setRefreshError("");
    } catch (caught) {
      if (version !== requestVersion.current || controller.signal.aborted) return;
      if (background && hasLoaded.current) {
        setRefreshError(loadErrorMessage(caught, "订单刷新失败"));
      } else {
        setError(loadErrorMessage(caught, "订单台账加载失败"));
      }
    } finally {
      if (version === requestVersion.current) {
        activeRequest.current = null;
        setLoading(false);
      }
    }
  }, [canReadCustomers, focusedOrderId]);

  function load(nextFilters = filters) {
    // Background refresh uses the last submitted query, not partly typed filters.
    appliedParams.current = new URLSearchParams(Object.entries(nextFilters).filter(([, value]) => value)).toString();
    return refreshOrders();
  }

  function applyDatePreset(preset: OrderDatePreset) {
    const nextFilters = { ...filters, ...orderDateRange(preset) };
    setFilters(nextFilters);
    void load(nextFilters);
  }

  useEffect(() => {
    void refreshOrders(hasLoaded.current);
    return () => {
      requestVersion.current += 1;
      activeRequest.current?.abort();
      activeRequest.current = null;
    };
  }, [refreshOrders, refreshVersion]);

  const refreshPaused = Boolean(paymentOrder || shipmentOrder || cancelOrder || reverseTarget || printOrder || advancingId);
  useEffect(() => {
    if (refreshPaused) return;
    const refreshVisible = () => {
      if (document.visibilityState !== "hidden") void refreshOrders(true);
    };
    const timer = window.setInterval(refreshVisible, 10_000);
    window.addEventListener("focus", refreshVisible);
    window.addEventListener("online", refreshVisible);
    document.addEventListener("visibilitychange", refreshVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshVisible);
      window.removeEventListener("online", refreshVisible);
      document.removeEventListener("visibilitychange", refreshVisible);
    };
  }, [refreshOrders, refreshPaused]);
  async function exportOrders() {
    const params = new URLSearchParams(
      Object.entries(filters).filter(([, value]) => value),
    );
    try {
      saveBlob(
        await download(`/api/v1/admin/orders/export?${params}`),
        `龙杰订单台账-${todayInShanghai()}.xlsx`,
      );
    } catch (caught) {
      notify(loadErrorMessage(caught, "订单导出失败"), "error");
    }
  }
  async function advanceFulfillment(
    order: Order,
    status: FulfillmentStatus,
  ) {
    if (
      status === "COMPLETED" &&
      !window.confirm(
        `确认订单 ${order.orderNo} 已全额回款并完结？`,
      )
    ) {
      return;
    }
    setAdvancingId(order.id);
    try {
      await request(`/api/v1/admin/orders/${order.id}/fulfillment`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      notify(`订单已更新为“${fulfillmentLabel(status, order.deliveryMethod).label}”`);
      await load();
    } catch (caught) {
      notify(loadErrorMessage(caught, "订单流程更新失败"), "error");
      await load();
    } finally {
      setAdvancingId(null);
    }
  }
  const update = (key: keyof typeof filters, value: string) =>
    setFilters((current) => ({ ...current, [key]: value }));
  return (
    <>
      {focusOrder && (
        <section className="order-operations__created" aria-label="刚开出的订单">
          <div>
            <h2>刚开出的订单：{focusOrder.orderNo}</h2>
            <p>在本单确认出货、登记收款或打印订单。</p>
          </div>
          <span>
            <Button variant="secondary" onClick={onViewAllOrders}>查看全部订单</Button>
            {canCreateOrders && <Button onClick={onPlaceOrder}><Plus />继续开单</Button>}
          </span>
        </section>
      )}
      {!focusOrder && <section className="admin-toolbar order-toolbar">
        <div className="toolbar-search">
          <MagnifyingGlass />
          <input
            value={filters.query}
            onChange={(e) => update("query", e.target.value)}
            placeholder="搜索订单号或客户名称"
            aria-label="搜索订单号或客户名称"
          />
        </div>
        <Button variant="secondary" className="order-toolbar__query" onClick={() => void load()}>
          <MagnifyingGlass />查询
        </Button>
        <MobileDisclosure title="筛选订单" className="order-toolbar__filters"
          description={Object.entries(filters).filter(([key, value]) => key !== "query" && value).length
            ? `已设置 ${Object.entries(filters).filter(([key, value]) => key !== "query" && value).length} 项条件`
            : "客户、状态和日期"}>
        <div className="order-toolbar__filter-fields">
        {canReadCustomers && <select
          value={filters.customerId}
          onChange={(e) => update("customerId", e.target.value)}
          aria-label="筛选客户"
        >
          <option value="">全部客户</option>
          {customers.map((customer) => (
            <option key={customer.id} value={customer.id}>
              {customer.name}
            </option>
          ))}
        </select>}
        <select
          value={filters.status}
          onChange={(e) => update("status", e.target.value)}
          aria-label="筛选单据状态"
        >
          <option value="">全部单据状态</option>
          <option value="ACTIVE">正常</option>
          <option value="CANCELLED">已取消</option>
        </select>
        <select
          value={filters.paymentStatus}
          onChange={(e) => update("paymentStatus", e.target.value)}
          aria-label="筛选收款状态"
        >
          <option value="">全部收款状态</option>
          <option value="UNPAID">未收款</option>
          <option value="PARTIAL">部分收款</option>
          <option value="PAID">已收款</option>
        </select>
        <select
          value={filters.fulfillmentStatus}
          onChange={(e) => update("fulfillmentStatus", e.target.value)}
          aria-label="筛选履约状态"
        >
          <option value="">全部履约状态</option>
          <option value="CONFIRMED">订单确认</option>
          <option value="PICKING">拣货中 / 备货中</option>
          <option value="DISPATCHED">已出车 / 已提货</option>
          <option value="COMPLETED">已完结</option>
        </select>
        <div className="order-toolbar__dates" role="group" aria-label="下单日期筛选">
          <div className="order-toolbar__date-fields">
            <label>
              <span>开始日期</span>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => update("dateFrom", e.target.value)}
                aria-label="开始日期"
              />
            </label>
            <label>
              <span>结束日期</span>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => update("dateTo", e.target.value)}
                aria-label="结束日期"
              />
            </label>
          </div>
          <div className="order-toolbar__presets" role="group" aria-label="快捷日期">
            {orderDatePresets.map((preset) => {
              const range = orderDateRange(preset.value);
              const selected = filters.dateFrom === range.dateFrom && filters.dateTo === range.dateTo;
              return <Button key={preset.value} variant={selected ? "primary" : "secondary"}
                aria-pressed={selected} onClick={() => applyDatePreset(preset.value)}>{preset.label}</Button>;
            })}
          </div>
        </div>
        </div>
        </MobileDisclosure>
        {canExportOrders && <Button variant="secondary" onClick={() => void exportOrders()}>
          <DownloadSimple />
          导出 Excel
        </Button>}
        {canCreateOrders && <Button onClick={onPlaceOrder}><Plus />代客下单</Button>}
      </section>}
      {refreshError && !loading && !error && (
        <div className="inline-error" role="status">
          自动刷新失败：{refreshError}。当前显示上次加载的订单，将在下次刷新时重试。
          <Button variant="secondary" onClick={() => void refreshOrders(true)}>立即重试</Button>
        </div>
      )}
      {loading ? (
        <LoadingRows />
      ) : error ? (
        <LoadError title="订单台账暂时无法加载" description={`${error}。请检查网络连接后重试。`} onRetry={() => void load()} />
      ) : orders.length === 0 ? (
        <EmptyState
          title="没有匹配订单"
          description="调整筛选条件后重新查询。"
        />
      ) : (
        <section className="order-ledger">
          {orders.map((order) => {
            const paid = paymentLabel(order.paymentStatus!);
            const fulfillment = fulfillmentLabel(order.fulfillmentStatus, order.deliveryMethod);
            return (
              <article
                key={order.id}
                className={
                  openId === order.id ? "ledger-order open" : "ledger-order"
                }
              >
                <button
                  className="ledger-order__summary"
                  aria-expanded={openId === order.id}
                  onClick={() =>
                    setOpenId(openId === order.id ? null : order.id)
                  }
                >
                  <span className="order-number">
                    <small>{formatDateTime(order.createdAt)}</small>
                    <b>{order.orderNo}</b>
                    <small className="order-mobile-customer">客户：{order.customerName}{order.deliveryMethod === "SELF_PICKUP" ? " · 自提" : ""}</small>
                  </span>
                  <span>
                    <small>客户</small>
                    <b>{order.customerName}</b>
                    <em>{order.customerCode}</em>
                  </span>
                  <span>
                    <small>订单金额</small>
                    <MoneyValue cents={order.totalCents} />
                  </span>
                  <span>
                    <small>未收余额</small>
                    <MoneyValue cents={order.outstandingCents!} />
                  </span>
                  <span className="order-statuses">
                    {order.status === "CANCELLED" ? (
                      <StatusBadge tone="danger">已取消</StatusBadge>
                    ) : (
                      <StatusBadge tone={fulfillment.tone}>
                        {fulfillment.label}
                      </StatusBadge>
                    )}
                    <StatusBadge tone={paid.tone}>{paid.label}</StatusBadge>
                  </span>
                  <CaretDown />
                </button>
                {openId === order.id && (
                  <div className="ledger-order__detail">
                    {order.orderSource && (
                      <p className="order-note">
                        下单方式：{order.orderSource === "STAFF" ? "管理员代客下单" : "客户自主下单"}
                        {order.createdByName && ` · 操作人：${order.createdByName}`}
                      </p>
                    )}
                    {order.customerSnapshot && (
                      <p className="order-note">
                        {order.customerId ? "客户信息" : "临时客户"}：{order.customerSnapshot.name}
                        {order.customerSnapshot.contactName && ` · 联系人：${order.customerSnapshot.contactName}`}
                        {order.customerSnapshot.phone && ` · 电话：${order.customerSnapshot.phone}`}
                      </p>
                    )}
                    <div className="order-actions">
                      <div>
                        <p>
                          {order.addressSnapshot.contactName} ·{" "}
                          {order.addressSnapshot.phone}
                        </p>
                        <span>
                          {order.deliveryMethod === "SELF_PICKUP" ? "到店自提" : <>
                            {order.addressSnapshot.province}
                            {order.addressSnapshot.city}
                            {order.addressSnapshot.district}
                            {order.addressSnapshot.detail}
                          </>}
                        </span>
                      </div>
                      <span>
                        <Button variant="secondary" onClick={() => setPrintOrder(order)}>
                          <Printer />
                          打印订单
                        </Button>
                        {(isAdmin || canManagePayments) && order.status === "ACTIVE" && (
                          <>
                          {canManagePayments && order.fulfillmentStatus === "DISPATCHED" &&
                            order.outstandingCents! > 0 && (
                            <Button onClick={() => setPaymentOrder(order)}>
                              <Money />
                              登记收款
                            </Button>
                          )}
                          {isAdmin && order.fulfillmentStatus === "CONFIRMED" && (
                            <Button
                              variant="secondary"
                              loading={advancingId === order.id}
                              onClick={() => void advanceFulfillment(order, "PICKING")}
                            >
                              <Package />
                              {order.deliveryMethod === "SELF_PICKUP" ? "开始备货" : "开始拣货"}
                            </Button>
                          )}
                          {isAdmin && (order.fulfillmentStatus === "CONFIRMED" || order.fulfillmentStatus === "PICKING") && (
                            <Button
                              disabled={advancingId !== null}
                              onClick={() => setShipmentOrder(order)}
                            >
                              <Check />
                              出货
                            </Button>
                          )}
                          {isAdmin && order.fulfillmentStatus === "DISPATCHED" &&
                            order.outstandingCents === 0 && (
                              <Button
                                loading={advancingId === order.id}
                                onClick={() => void advanceFulfillment(order, "COMPLETED")}
                              >
                                <Check />
                                确认已回款并完结
                              </Button>
                            )}
                          {isAdmin && (order.fulfillmentStatus === "CONFIRMED" ||
                            order.fulfillmentStatus === "PICKING") && (
                            <Button
                              variant="danger"
                              onClick={() => setCancelOrder(order)}
                            >
                              取消订单
                            </Button>
                          )}
                          </>
                        )}
                      </span>
                    </div>
                    {order.inventoryDocument && <p className="order-note">销售出库单：{order.inventoryDocument.documentNo} · 已按本单数量扣减库存</p>}
                    {order.fulfillmentStatus === "COMPLETED" && order.completedAt && (
                      <p className="order-note">
                        已于 {formatDateTime(order.completedAt)} 确认回款并完结。
                      </p>
                    )}
                    {order.status === "CANCELLED" && (
                      <div className="cancel-note">
                        取消原因：{order.cancellationReason}
                      </div>
                    )}
                    <div className="order-item-table order-operations__items">
                      <div>
                        <span>商品 / 规格</span>
                        <span>数量</span>
                        <span>成交单价</span>
                        <span>金额</span>
                      </div>
                      {order.items.map((item) => (
                        <div key={item.id}>
                          <span>
                            <b>{item.productNameSnapshot}</b>
                            <small>
                              {item.specificationSnapshot} ·{" "}
                              {item.skuCodeSnapshot}
                            </small>
                          </span>
                          <span>
                            {item.quantity} {item.unitSnapshot}
                          </span>
                          <MoneyValue cents={item.unitPriceCents} />
                          <MoneyValue cents={item.lineTotalCents} />
                        </div>
                      ))}
                    </div>
                    <div className="payment-ledger">
                      <h3>收款流水</h3>
                      {order.payments?.length ? (
                        order.payments.map((payment) => (
                          <div
                            key={payment.id}
                            className={
                              payment.entryType === "REVERSAL" ? "reversal" : ""
                            }
                          >
                            <span>
                              <b>
                                {payment.entryType === "PAYMENT"
                                  ? payment.method
                                  : "冲销"}
                              </b>
                              <small>
                                {formatDate(payment.paymentDate)} ·{" "}
                                {payment.referenceNo || "无凭证号"}
                              </small>
                              <small>登记时间：{formatDateTime(payment.createdAt)} · 操作人：{payment.recordedByName || "未记录"}</small>
                            </span>
                            <MoneyValue cents={payment.amountCents} />
                            <span>{payment.note || "—"}</span>
                            {canManagePayments &&
                            payment.entryType === "PAYMENT" &&
                            !order.payments?.some(
                              (row) => row.reversalOfId === payment.id,
                            ) ? (
                              <Button
                                variant="ghost"
                                onClick={() =>
                                  setReverseTarget({
                                    order,
                                    paymentId: payment.id,
                                  })
                                }
                              >
                                <ArrowClockwise />
                                冲销
                              </Button>
                            ) : (
                              <span />
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="muted-copy">尚未登记收款。</p>
                      )}
                    </div>
                    {order.note && (
                      <p className="order-note">订单备注：{order.note}</p>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </section>
      )}
      {printOrder && <OrderPrintPreview order={printOrder} canEditSettings={canConfigurePrint} onClose={() => setPrintOrder(null)} />}
      {shipmentOrder && (
        <ShipmentModal
          order={shipmentOrder}
          inventoryEnabled={inventoryEnabled}
          onPendingChange={onPendingChange}
          onClose={() => setShipmentOrder(null)}
          onDone={(message, tone = "success") => {
            setShipmentOrder(null);
            notify(message, tone);
            void load();
          }}
        />
      )}
      {paymentOrder && (
        <PaymentModal
          order={paymentOrder}
          onPendingChange={onPendingChange}
          onClose={() => setPaymentOrder(null)}
          onDone={(completed) => {
            setPaymentOrder(null);
            notify(
              completed
                ? "回款已确认，订单已完结，订单台账保留记录"
                : "部分回款已登记，剩余回款确认后自动完结",
            );
            void load();
          }}
        />
      )}
      {cancelOrder && (
        <ReasonModal
          title="取消订单"
          description={`订单 ${cancelOrder.orderNo} 将永久标记为已取消。已有收款时必须先全部冲销。`}
          label="取消原因"
          action="确认取消"
          danger
          onClose={() => setCancelOrder(null)}
          onSubmit={async (reason) => {
            await request(`/api/v1/admin/orders/${cancelOrder.id}/cancel`, {
              method: "POST",
              body: JSON.stringify({ reason }),
            });
            setCancelOrder(null);
            notify("订单已取消");
            await load();
          }}
        />
      )}
      {reverseTarget && (
        <ReasonModal
          title="冲销收款"
          description={
            reverseTarget.order.fulfillmentStatus === "COMPLETED"
              ? `冲销后，订单 ${reverseTarget.order.orderNo} 将恢复为“${fulfillmentLabel("DISPATCHED", reverseTarget.order.deliveryMethod).label}”；系统会追加一笔等额负数流水，原记录不会删除，订单台账保留记录。`
              : "系统会追加一笔等额负数流水，原记录不会删除。"
          }
          label="冲销原因"
          action={
            reverseTarget.order.fulfillmentStatus === "COMPLETED"
              ? "确认冲销并重开订单"
              : "确认冲销"
          }
          danger
          onClose={() => setReverseTarget(null)}
          onSubmit={async (reason) => {
            const reopensOrder =
              reverseTarget.order.fulfillmentStatus === "COMPLETED";
            await request(
              `/api/v1/admin/payments/${reverseTarget.paymentId}/reverse`,
              { method: "POST", body: JSON.stringify({ reason }) },
            );
            setReverseTarget(null);
            notify(
              reopensOrder
                ? "收款已冲销，订单已恢复为“已出车”"
                : "收款已冲销",
            );
            await load();
          }}
        />
      )}
    </>
  );
}

type AdminSectionProps = {
  isAdmin: boolean;
  notify: (message: string, tone?: "success" | "error" | "info") => void;
};

function useAccountListRefresh<T>(path: string, key: "customers" | "staff", update: (accounts: T[]) => void) {
  useEffect(() => {
    let active = true;
    const refresh = () => {
      if (document.visibilityState === "hidden") return;
      void request<Record<string, T[]>>(path)
        .then((result) => {
          const accounts = result[key];
          if (active && accounts) update(accounts);
        })
        .catch(() => { /* Keep the current list when a background refresh fails. */ });
    };
    const timer = window.setInterval(refresh, 30_000);
    window.addEventListener("focus", refresh);
    window.addEventListener("account-credentials-changed", refresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("account-credentials-changed", refresh);
    };
  }, [path, key, update]);
}

function CustomersAdmin({ isAdmin, notify, canConfigurePermissions }: AdminSectionProps & { canConfigurePermissions: boolean }) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [permissionCustomer, setPermissionCustomer] = useState<Customer | null>(null);
  const [password, setPassword] = useState<{
    customer: string;
    value: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  async function load() {
    setAppliedQuery(query);
    setLoading(true);
    setError("");
    try {
      const result = await request<{ customers: Customer[] }>(
        `/api/v1/admin/customers?query=${encodeURIComponent(query)}`,
      );
      setCustomers(result.customers);
    } catch (caught) {
      setError(loadErrorMessage(caught, "客户账号加载失败"));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void request<{ customers: Customer[] }>("/api/v1/admin/customers")
      .then((result) => setCustomers(result.customers))
      .catch((caught: unknown) => setError(loadErrorMessage(caught, "客户账号加载失败")))
      .finally(() => setLoading(false));
  }, []);
  useAccountListRefresh<Customer>(`/api/v1/admin/customers?query=${encodeURIComponent(appliedQuery)}`, "customers", setCustomers);
  return (
    <>
      <section className="admin-toolbar account-toolbar account-toolbar--customers">
        <div className="toolbar-search">
          <MagnifyingGlass />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜索客户名称、编码或登录名"
            aria-label="搜索客户名称、编码或登录名"
          />
        </div>
        <Button variant="secondary" onClick={() => void load()}>
          查询
        </Button>
        {isAdmin && (
          <Button onClick={() => setCreating(true)}>
            <Plus />
            新建客户
          </Button>
        )}
      </section>
      {loading ? (
        <LoadingRows />
      ) : error ? (
        <LoadError title="客户账号暂时无法加载" description={`${error}。请检查网络连接后重试。`} onRetry={() => void load()} />
      ) : <section className="admin-section flush">
        <div className="data-table customer-table">
          <div className="data-table__head">
            <span>客户</span>
            <span>登录账号</span>
            <span>联系人</span>
            <span>历史订单</span>
            <span>状态</span>
            <span>操作</span>
          </div>
          {customers.map((customer) => (
            <div className="data-table__row" key={customer.id}>
              <span data-label="客户">
                <b>{customer.name}</b>
                <small>{customer.code}</small>
              </span>
              <span data-label="登录账号">
                <b>{customer.username}</b>
                <small>密码更新：{customer.passwordChangedAt ? formatDateTime(customer.passwordChangedAt) : "未记录"}</small>
                {customer.mustChangePassword && (
                  <small className="warning-text">等待修改初始密码</small>
                )}
              </span>
              <span data-label="联系人">
                {customer.contactName}
                <small>{customer.phone}</small>
              </span>
              <span data-label="历史订单">{customer.orderCount} 单</span>
              <span data-label="状态"><span className="account-status-controls"><StatusBadge tone={customer.active ? "success" : "danger"}>
                  {customer.active ? "启用" : "停用"}
                </StatusBadge>
                {isAdmin && <Button
                  variant="ghost"
                  className={`account-status-toggle account-status-toggle--${customer.active ? "disable" : "enable"}`}
                  onClick={async () => {
                    await request(`/api/v1/admin/customers/${customer.id}`, {
                      method: "PATCH",
                      body: JSON.stringify({ active: !customer.active }),
                    });
                    notify(customer.active ? "客户已停用" : "客户已启用");
                    await load();
                  }}
                >{customer.active ? "停用" : "启用"}</Button>}
                </span></span>
              <span data-label="操作">
                {canConfigurePermissions && <Button variant="ghost" onClick={() => setPermissionCustomer(customer)}>设置权限</Button>}
                {isAdmin && <Button variant="ghost" onClick={() => setEditingCustomer(customer)}>编辑</Button>}
              </span>
            </div>
          ))}
        </div>
      </section>}
      {creating && (
        <CustomerModal
          canConfigurePermissions={canConfigurePermissions}
          onClose={() => setCreating(false)}
          onCreated={(name, temporaryPassword) => {
            setCreating(false);
            setPassword({ customer: name, value: temporaryPassword });
            void load();
          }}
        />
      )}
      {permissionCustomer && <DealerPermissionsModal account={permissionCustomer} onClose={() => setPermissionCustomer(null)} onSaved={() => {
        setPermissionCustomer(null);
        notify("客户账号权限已保存");
        void load();
      }} />}
      {editingCustomer && <CustomerEditorModal customer={editingCustomer} canManageCredentials={canConfigurePermissions}
        onClose={() => setEditingCustomer(null)} onSaved={() => {
          setEditingCustomer(null);
          notify("客户资料已更新");
          void load();
        }} />}
      {password && (
        <Modal
          title="一次性初始密码"
          description={`${password.customer} 的密码只在此处显示一次，请安全交给客户。`}
          onClose={() => setPassword(null)}
          footer={
            <Button
              onClick={() => {
                void navigator.clipboard.writeText(password.value);
                notify("初始密码已复制");
              }}
            >
              复制密码
            </Button>
          }
        >
          <div className="temporary-password">
            <small>初始密码</small>
            <code>{password.value}</code>
            <p>客户首次登录后必须立即修改密码。</p>
          </div>
        </Modal>
      )}
    </>
  );
}

function StaffAdmin({
  currentUserId,
  isAdmin,
  notify,
}: AdminSectionProps & { currentUserId: string }) {
  const [staff, setStaff] = useState<StaffAccount[]>([]);
  const [creating, setCreating] = useState(false);
  const [credentialsAccount, setCredentialsAccount] = useState<StaffAccount | null>(null);
  const [permissionAccount, setPermissionAccount] = useState<StaffAccount | null>(null);
  const [password, setPassword] = useState<{
    account: string;
    value: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  async function load() {
    setLoading(true);
    setError("");
    try {
      const result = await request<{ staff: StaffAccount[] }>(
        "/api/v1/admin/staff",
      );
      setStaff(result.staff);
    } catch (caught) {
      setError(loadErrorMessage(caught, "内部账号加载失败"));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void request<{ staff: StaffAccount[] }>("/api/v1/admin/staff")
      .then((result) => setStaff(result.staff))
      .catch((caught: unknown) => setError(loadErrorMessage(caught, "内部账号加载失败")))
      .finally(() => setLoading(false));
  }, []);
  useAccountListRefresh<StaffAccount>("/api/v1/admin/staff", "staff", setStaff);
  return (
    <>
      <section className="admin-toolbar account-toolbar account-toolbar--staff">
        <div className="toolbar-search">
          <IdentificationCard />
          <input
            disabled
            value="管理员可创建和维护内部账号"
            aria-label="内部账号说明"
          />
        </div>
        <Button variant="secondary" onClick={() => void load()}>刷新</Button>
        {isAdmin && (
          <Button onClick={() => setCreating(true)}>
            <Plus />
            新建内部账号
          </Button>
        )}
      </section>
      {loading ? (
        <LoadingRows />
      ) : error ? (
        <LoadError title="内部账号暂时无法加载" description={`${error}。请检查网络连接后重试。`} onRetry={() => void load()} />
      ) : <section className="admin-section flush">
        <div className="data-table staff-table">
          <div className="data-table__head">
            <span>内部人员</span>
            <span>登录账号</span>
            <span>角色</span>
            <span>最近登录</span>
            <span>状态</span>
            <span>操作</span>
          </div>
          {staff.map((account) => (
            <div className="data-table__row" key={account.id}>
              <span data-label="内部人员">
                <b>{account.displayName}</b>
                {account.id === currentUserId && <small>当前账号</small>}
              </span>
              <span data-label="登录账号">
                <b>{account.username}</b>
                <small>密码更新：{account.passwordChangedAt ? formatDateTime(account.passwordChangedAt) : "未记录"}</small>
                {account.mustChangePassword && (
                  <small className="warning-text">等待修改初始密码</small>
                )}
              </span>
              <span data-label="角色"><StatusBadge
                  tone={account.staffRole === "ADMIN" ? "info" : "neutral"}
                >
                  {account.staffRole === "ADMIN" ? "管理员" : account.staffRole === "CUSTOM" ? "自定义权限" : "查看员"}
                </StatusBadge></span>
              <span data-label="最近登录">
                {account.lastLoginAt
                  ? formatDateTime(account.lastLoginAt)
                  : "从未登录"}
              </span>
              <span data-label="状态"><span className="account-status-controls"><StatusBadge tone={account.active ? "success" : "danger"}>
                  {account.active ? "启用" : "停用"}
                </StatusBadge>
                {isAdmin && <Button
                  variant="ghost"
                  className={`account-status-toggle account-status-toggle--${account.active ? "disable" : "enable"}`}
                  disabled={account.id === currentUserId}
                  onClick={async () => {
                    await request(`/api/v1/admin/staff/${account.id}`, {
                      method: "PATCH",
                      body: JSON.stringify({ active: !account.active }),
                    });
                    notify(account.active ? "内部账号已停用" : "内部账号已启用");
                    await load();
                  }}
                >{account.active ? "停用" : "启用"}</Button>}
                </span></span>
              <span data-label="操作">
                {isAdmin && (
                  <>
                    <Button variant="ghost" disabled={account.id === currentUserId} title={account.id === currentUserId ? "不能修改当前登录管理员的权限" : undefined} onClick={() => setPermissionAccount(account)}>设置权限</Button>
                    <Button variant="ghost" disabled={account.id === currentUserId} title={account.id === currentUserId ? "不能在此修改当前登录管理员的账号密码" : undefined} onClick={() => setCredentialsAccount(account)}>修改账号密码</Button>
                    <Button
                      variant="ghost"
                      disabled={account.id === currentUserId}
                      onClick={async () => {
                        await request(`/api/v1/admin/staff/${account.id}`, {
                          method: "PATCH",
                          body: JSON.stringify({
                            staffRole:
                              account.staffRole === "ADMIN"
                                ? "VIEWER"
                                : "ADMIN",
                          }),
                        });
                        notify("内部账号角色已更新");
                        await load();
                      }}
                    >
                      {account.staffRole === "ADMIN"
                        ? "改为查看员"
                        : "升为管理员"}
                    </Button>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      </section>}
      {!isAdmin && (
        <div className="readonly-note">
          查看员只能查看内部账号，不能创建、修改账号密码或更改权限。
        </div>
      )}
      {permissionAccount && <StaffPermissionsModal account={permissionAccount} onClose={() => setPermissionAccount(null)} onSaved={() => {
        setPermissionAccount(null);
        notify("内部账号权限已保存");
        void load();
      }} />}
      {credentialsAccount && <AccountCredentialsModal name={credentialsAccount.displayName} username={credentialsAccount.username}
        viewPasswordPath={`/api/v1/admin/staff/${credentialsAccount.id}/password`}
        path={`/api/v1/admin/staff/${credentialsAccount.id}/credentials`} onClose={() => setCredentialsAccount(null)} onSaved={() => {
          setCredentialsAccount(null);
          notify("内部登录账号和密码已更新");
          void load();
        }} />}
      {creating && (
        <StaffModal
          onClose={() => setCreating(false)}
          onCreated={(account, temporaryPassword) => {
            setCreating(false);
            setPassword({ account, value: temporaryPassword });
            void load();
          }}
        />
      )}
      {password && (
        <Modal
          title="一次性初始密码"
          description={`${password.account} 的密码只在此处显示一次。`}
          onClose={() => setPassword(null)}
          footer={
            <Button
              onClick={() => {
                void navigator.clipboard.writeText(password.value);
                notify("初始密码已复制");
              }}
            >
              复制密码
            </Button>
          }
        >
          <div className="temporary-password">
            <small>初始密码</small>
            <code>{password.value}</code>
            <p>内部人员首次登录后必须立即修改。</p>
          </div>
        </Modal>
      )}
    </>
  );
}

const ADMIN_PRODUCT_BATCH_SIZE = 30;

function ProductsAdmin({ isAdmin, notify, canManageCategories, canManagePromotions }: AdminSectionProps & { canManageCategories: boolean; canManagePromotions: boolean }) {
  const isPhone = usePhoneLayout();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryNodes, setCodeCategories] = useState<CodeCategoryNode[] | undefined>();
  const codeCategories = useSynchronizedCodeCategories(categoryNodes);
  const [categoryId, setCategoryId] = useState("");
  const [mobileAllProducts, setMobileAllProducts] = useState(false);
  const [visibleProductCount, setVisibleProductCount] = useState(
    ADMIN_PRODUCT_BATCH_SIZE,
  );
  const [productModal, setProductModal] = useState(false);
  const [promotionProduct, setPromotionProduct] = useState<Product | null>(null);
  const [categoryProduct, setCategoryProduct] = useState<Product | null>(null);
  const [categoryEditor, setCategoryEditor] = useState<CodeCategoryNode | null>(null);
  const [skuProduct, setSkuProduct] = useState<Product | null>(null);
  const [editingSku, setEditingSku] = useState<{
    product: Product;
    sku?: Sku;
  } | null>(null);
  const [imageProduct, setImageProduct] = useState<Product | null>(null);
  const [updatingProductId, setUpdatingProductId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [productError, setProductError] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const error = productError || categoryError;
  const loadSequence = useRef(0);
  const categoryLoadSequence = useRef(0);
  const productController = useRef<AbortController | null>(null);
  const categoryController = useRef<AbortController | null>(null);
  const productSearch = useProductSearch({ onCommit: () => {
    setVisibleProductCount(ADMIN_PRODUCT_BATCH_SIZE);
    setLoading(true);
    setProductError("");
  } });
  const { query, revision: searchRevision } = productSearch;
  const visibleCategoryId = effectiveProductCategory(query, categoryId);
  const loadProducts = useCallback(async () => {
    const sequence = ++loadSequence.current;
    productController.current?.abort();
    const controller = new AbortController();
    productController.current = controller;
    try {
      const productResult = await request<{ products: Product[] }>(
        productSearchPath("/api/v1/admin/products", query, categoryId), { signal: controller.signal },
      );
      if (!controller.signal.aborted && sequence === loadSequence.current) {
        setProductError("");
        setProducts(productResult.products);
      }
    } catch (caught) {
      if (!controller.signal.aborted && sequence === loadSequence.current) {
        setProductError(loadErrorMessage(caught, "商品目录加载失败"));
      }
    } finally {
      if (!controller.signal.aborted && sequence === loadSequence.current) setLoading(false);
    }
  }, [query, categoryId]);
  const loadCategories = useCallback(async () => {
    const sequence = ++categoryLoadSequence.current;
    categoryController.current?.abort();
    const controller = new AbortController();
    categoryController.current = controller;
    try {
      const result = await request<{ categories: Category[]; codeCategories?: CodeCategoryNode[] }>(
        "/api/v1/admin/categories?includeProducts=false", { signal: controller.signal },
      );
      if (!controller.signal.aborted && sequence === categoryLoadSequence.current) {
        setCategoryError("");
        setCategories(result.categories);
        setCodeCategories(result.codeCategories);
      }
    } catch (caught) {
      if (!controller.signal.aborted && sequence === categoryLoadSequence.current) {
        setCategoryError(loadErrorMessage(caught, "商品分类加载失败"));
      }
    }
  }, []);
  const load = useCallback(() => Promise.all([loadProducts(), loadCategories()]), [loadProducts, loadCategories]);
  useEffect(() => {
    let active = true;
    void Promise.resolve().then(() => { if (active) return loadProducts(); });
    return () => { active = false; loadSequence.current += 1; productController.current?.abort(); };
  }, [loadProducts, searchRevision]);
  useEffect(() => {
    let active = true;
    void Promise.resolve().then(() => { if (active) return loadCategories(); });
    return () => { active = false; categoryLoadSequence.current += 1; categoryController.current?.abort(); };
  }, [loadCategories]);
  useCatalogRefresh(() => { void load(); });
  const selectedCategory = codeCategories?.find(node => `code:${node.prefix}` === visibleCategoryId);
  const browsingCategories = isPhone && !mobileAllProducts && !query && Boolean(codeCategories?.length);
  const hideRootProducts = browsingCategories && !visibleCategoryId;
  const visibleProducts = browsingCategories && selectedCategory
    ? products.filter(product => getProductCategoryPrefixes(product.code).at(-1) === selectedCategory.prefix)
    : products;
  const hasChildren = selectedCategory && codeCategories?.some(node => node.parentPrefix === selectedCategory.prefix);
  function selectPhoneCategory(value: string, allProducts = false) {
    productSearch.reset("");
    setMobileAllProducts(allProducts);
    const next = value === "all" ? "" : value;
    if (next !== categoryId) { setCategoryId(next); setLoading(true); setProductError(""); }
    setVisibleProductCount(ADMIN_PRODUCT_BATCH_SIZE);
    window.scrollTo({ top: 0, behavior: "instant" });
  }
  function renderProductActions(product: Product, phoneSku?: Sku) {
    if (!isAdmin) return null;
    return (<div className={isPhone ? "staff-catalog-actions" : "product-admin-actions"} role="group" aria-label={`${product.code} 商品操作`}>
                {(isPhone || product.skus.length <= 1) && <Button variant="ghost" onClick={() => setEditingSku({ product, ...((phoneSku ?? product.skus[0]) ? { sku: phoneSku ?? product.skus[0] } : {}) })}>
                  <PencilSimple />编辑
                </Button>}
                <MobileDisclosure title="更多操作" className="product-extra-actions">
                <Button variant="secondary" onClick={() => setCategoryProduct(product)}>
                  <Folders />{getProductCategoryPrefixes(product.code).length ? "名称分组" : "修改分类"}
                </Button>
                {canManagePromotions && <Button variant="secondary" onClick={() => setPromotionProduct(product)}>
                  <Tag />设折扣
                </Button>}
                <Button
                  variant="secondary"
                  onClick={() => setSkuProduct(product)}
                >
                  <Plus />
                  新增规格
                </Button>
                <Button variant="secondary" onClick={() => setImageProduct(product)}>
                  <UploadSimple />主图
                </Button>
                <Button
                  variant="ghost"
                  disabled={updatingProductId === product.id}
                  onClick={async () => {
                    setUpdatingProductId(product.id);
                    try {
                      await request(`/api/v1/admin/products/${product.id}`, {
                        method: "PATCH",
                        body: JSON.stringify({ active: !product.active }),
                      });
                      notify(product.active ? "商品已下架" : "商品已上架");
                    } catch (caught) {
                      notify(caught instanceof ApiError ? caught.message : "商品状态更新失败", "error");
                    } finally {
                      setUpdatingProductId(null);
                    }
                  }}
                >
                  {product.active ? "下架" : "上架"}
                </Button>
                </MobileDisclosure>
              </div>);
  }
  return (
    <>
      {isPhone && <section className="staff-catalog staff-catalog-toolbar" aria-label="查找目录商品">
        <ProductSearch search={productSearch} ariaLabel="搜索商品、商品编码或 SKU" busy={loading} />
      </section>}
      <DealerCatalogLayout enabled className="inventory-category-workspace category-workspace staff-catalog" storageKey="longjie.admin-category-width"
        sidebarId="product-catalog-category-panel" contentId="product-catalog-product-panel" defaultWidth={420} minWidth={340} contentMinWidth={440}>
      <aside id="product-catalog-category-panel" className="inventory-category-sidebar category-navigation" aria-label="商品目录分类">
        {isPhone ? <>
          <MobileCategoryPicker nodes={codeCategories ?? []} legacyCategories={categories} selectedValue={visibleCategoryId || "all"} showLegacyCategories
            compact={Boolean(query) || mobileAllProducts} showAllProducts={mobileAllProducts && !query}
            headingAction={<>
              {isAdmin && <Button variant="secondary" aria-label="新建商品" title="新建商品" onClick={() => setProductModal(true)}><Plus />{!selectedCategory && <span>新建</span>}</Button>}
              {canManageCategories && selectedCategory && <Button variant="secondary" aria-label="编辑当前分类" title="编辑当前分类" onClick={() => setCategoryEditor(selectedCategory)}><PencilSimple /></Button>}
            </>}
            onShowAllProducts={() => selectPhoneCategory("all", true)} onSelect={selectPhoneCategory} />
        </> : <MobileDisclosure title="商品分类" resetKey={visibleCategoryId}
          description={visibleCategoryId.startsWith("code:") ? selectedCategory?.name ?? visibleCategoryId.slice(5) : categories.find(category => category.id === visibleCategoryId)?.name ?? "全部商品"}>
          <ProductCategoryFilter nodes={codeCategories ?? []} legacyCategories={categories} value={visibleCategoryId} allProductsInToolbar
            isAdmin={canManageCategories} {...(canManageCategories ? { onEditCategory: setCategoryEditor } : {})}
            onChange={(value) => { if (value === categoryId) return; setCategoryId(value); setLoading(true); setProductError(""); setVisibleProductCount(ADMIN_PRODUCT_BATCH_SIZE); }} />
        </MobileDisclosure>}
      </aside>
      <div id="product-catalog-product-panel" className="inventory-category-content">
      {!isPhone && <section className="admin-toolbar products-admin-toolbar">
        <ProductSearch search={productSearch} ariaLabel="搜索商品、商品编码或 SKU" buttonLabel="查询" />
        {isAdmin && (
          <Button onClick={() => setProductModal(true)}>
            <Plus />
            新建商品
          </Button>
        )}
      </section>}
      {isPhone && <div className="staff-catalog-summary"><h2>{query ? "搜索结果" : hideRootProducts ? "按分类查找商品" : hasChildren ? "本级直属规格" : "商品规格"}</h2>
        <p>{hideRootProducts ? "逐级打开分类，或输入编号查找" : hasChildren && visibleProducts.length === 0 ? "请选择下级分类查看规格" : `共 ${visibleProducts.reduce((count, product) => count + product.skus.length, 0)} 个规格`}</p></div>}
      {loading ? (
        <LoadingRows />
      ) : error ? (
        <LoadError title="商品目录暂时无法加载" description={`${error}。请检查网络连接后重试。`} onRetry={() => void load()} />
      ) : hideRootProducts || (browsingCategories && hasChildren && visibleProducts.length === 0) ? null : <>
      {isPhone ? <section className="mobile-sku-list" aria-label="目录商品规格">
        {visibleProducts.slice(0, visibleProductCount).flatMap(product => (product.skus.length ? product.skus : [undefined]).map(sku => (
          <MobileSkuCard key={sku?.id ?? product.id} productId={product.id} imageUrl={product.imageUrl} code={product.code} name={product.name}
            specification={sku?.specification || "未填写"} unit={sku?.unit || "未填写"} skuCode={sku?.code} reserveImage>
            <div className="staff-catalog-status"><StatusBadge tone={product.active && sku?.active ? "success" : "neutral"}>{!product.active ? "下架" : !sku ? "待添加规格" : sku.active ? "上架" : "规格已下架"}</StatusBadge>
              {product.description && <span>{product.description}</span>}</div>
            <div className="mobile-sku__order"><span className="staff-catalog-price"><small>单价</small>{sku ? <MoneyValue cents={sku.priceCents} /> : <span className="money">—</span>}</span>{renderProductActions(product, sku)}</div>
          </MobileSkuCard>
        )))}
      </section> : <section className="product-admin-grid">
        {products.slice(0, visibleProductCount).map((product) => (
          <article key={product.id} className={`product-admin-card${isAdmin ? " product-admin-card--editable" : ""}`}>
            <header>
              <div className="product-image">
                <ProductImage productId={product.id} imageUrl={product.imageUrl} />
              </div>
              <div className="product-admin-info">
                <div className="product-admin-identity">
                  <b className="product-admin-code">{product.code}</b>
                  <small>{product.categoryName}</small>
                </div>
                <h2>{product.name}</h2>
                {product.description && <p>{product.description}</p>}
              </div>
              <StatusBadge tone={product.active ? "success" : "danger"}>
                {product.active ? "上架" : "下架"}
              </StatusBadge>
            </header>
            <div className="product-admin-skus">
              {product.skus.length === 0 && (
                <div>
                  <span className="sku-summary">
                    <b>规格：未填写</b>
                    <small>单位：未填写</small>
                  </span>
                  <span className="money" aria-label="单价未填写">—</span>
                </div>
              )}
              {product.skus.map((sku) => (
                <div key={sku.id}>
                  <span className="sku-summary">
                    <b>规格：{sku.specification}</b>
                    <small>
                      单位：{sku.unit}
                    </small>
                  </span>
                  <MoneyValue cents={sku.priceCents} />
                  {!sku.active && <StatusBadge tone="neutral">规格已下架</StatusBadge>}
                  {isAdmin && product.skus.length > 1 && (
                    <span className="sku-actions">
                      <Button
                        variant="ghost"
                        onClick={() => setEditingSku({ product, sku })}
                      >
                        <PencilSimple />
                        编辑
                      </Button>
                    </span>
                  )}
                </div>
              ))}
            </div>
            {renderProductActions(product)}
          </article>
        ))}
      </section>}
      {visibleProducts.length > 0 && (
        <div className="admin-catalog-progress">
          <span>
            已显示 {Math.min(visibleProductCount, visibleProducts.length)} / {visibleProducts.length} 个商品
          </span>
          {visibleProductCount < visibleProducts.length && (
            <Button
              variant="secondary"
              onClick={() =>
                setVisibleProductCount((count) =>
                  Math.min(count + ADMIN_PRODUCT_BATCH_SIZE, visibleProducts.length),
                )
              }
            >
              加载更多
            </Button>
          )}
        </div>
      )}
      {visibleProducts.length === 0 && (
        <EmptyState
          title={categoryId || query ? "没有匹配的商品" : "还没有商品"}
          description={categoryId || query ? "可切换分类或修改搜索条件；商品会按完整商品编码自动归入对应层级。" : "新建商品后再添加可订购的规格 SKU。"}
        />
      )}
      </>}
      </div>
      </DealerCatalogLayout>
      {categoryEditor && canManageCategories && <CodeCategoryEditor category={categoryEditor} onClose={() => setCategoryEditor(null)}
        onSaved={() => { setCategoryEditor(null); notify("分类已保存，所有商品入口共用"); }} />}
      {imageProduct && (
        <ProductImageEditor product={imageProduct} onClose={() => setImageProduct(null)} onSaved={(deleted) => {
          setImageProduct(null);
          notify(deleted ? "商品主图已删除，所有规格同步更新" : "商品主图已更新，所有规格共用");
        }} />
      )}
      {categoryProduct && (
        <ProductCategoryEditor product={categoryProduct} categories={categories} onClose={() => setCategoryProduct(null)} onSaved={() => {
          setCategoryProduct(null); notify("商品及其 SKU 的分类已更新");
        }} />
      )}
      {canManagePromotions && promotionProduct && (
        <PromotionEditor product={promotionProduct} onClose={() => setPromotionProduct(null)} onSaved={() => {
          setPromotionProduct(null);
          notify("单品促销已保存，可在限时促销中查看");
          void loadProducts();
        }} />
      )}
      {productModal && (
        <ProductModal
          categories={categories}
          codeCategories={codeCategories ?? []}
          initialCategory={visibleCategoryId}
          canManageCategories={canManageCategories}
          onClose={() => setProductModal(false)}
          onDone={() => {
            setProductModal(false);
            notify("商品已创建");
          }}
        />
      )}
      {skuProduct && (
        <SkuModal
          product={skuProduct}
          onClose={() => setSkuProduct(null)}
          onDone={(createdProductCode) => {
            setSkuProduct(null);
            notify(createdProductCode ? "同级商品已创建" : "SKU 已创建");
          }}
        />
      )}
      {editingSku && (
        <SkuModal
          product={editingSku.product}
          {...(editingSku.sku ? { sku: editingSku.sku } : {})}
          completeProduct={!editingSku.sku}
          onClose={() => setEditingSku(null)}
          onDone={(createdProductCode, action) => {
            setEditingSku(null);
            notify(action === "renamed" ? "商品编码已更新" : createdProductCode ? "已保存为独立商品" : editingSku.sku ? "SKU 信息已更新" : "商品规格已保存");
          }}
        />
      )}
    </>
  );
}

type AuditLog = {
  id: number;
  action: string;
  entityType: string;
  entityId: string;
  beforeData: Record<string, unknown> | null;
  afterData: Record<string, unknown> | null;
  ipAddress: string | null;
  createdAt: string;
  actorName: string | null;
  orderNo: string | null;
};

function auditText(
  data: Record<string, unknown> | null,
  key: string,
): string | null {
  const value = data?.[key];
  return typeof value === "string" && value ? value : null;
}

function auditAmount(
  data: Record<string, unknown> | null,
  key: string,
): number | null {
  const value = data?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function auditFulfillmentStatus(value: string | null): string {
  if (
    value === "CONFIRMED" ||
    value === "PICKING" ||
    value === "DISPATCHED" ||
    value === "COMPLETED"
  ) {
    return fulfillmentLabel(value).label;
  }
  return "未知状态";
}

function auditBusinessDetail(log: AuditLog): string {
  if (log.action === "CUSTOMER_EDITED") {
    const customer = log.afterData?.customer ?? log.beforeData?.customer;
    if (customer && typeof customer === "object") {
      const data = customer as Record<string, unknown>;
      const name = auditText(data, "name");
      const code = auditText(data, "code");
      if (name || code) return [name, code].filter(Boolean).join(" · ");
    }
  }
  const orderNo = log.orderNo ?? (log.entityType === "ORDER" ? log.entityId : null);
  if (log.action === "ORDER_FULFILLMENT_CHANGED") {
    return `${orderNo ?? "订单"} · ${auditFulfillmentStatus(auditText(log.beforeData, "fulfillmentStatus"))} → ${auditFulfillmentStatus(auditText(log.afterData, "fulfillmentStatus"))}`;
  }
  if (log.action === "ORDER_REOPENED_AFTER_PAYMENT_REVERSAL") {
    return `${orderNo ?? "订单"} · 已完结 → 已出车`;
  }
  if (log.action === "ORDER_CREATED" || log.action === "ORDER_CREATED_ON_BEHALF") {
    const totalCents = auditAmount(log.afterData, "totalCents");
    return totalCents === null
      ? orderNo ?? "订单"
      : `${orderNo ?? "订单"} · ${formatCny(totalCents)}`;
  }
  if (log.action === "ORDER_CANCELLED") {
    const reason = auditText(log.afterData, "reason");
    return `${orderNo ?? "订单"}${reason ? ` · ${reason}` : ""}`;
  }
  if (log.action === "PAYMENT_RECORDED") {
    const amountCents = auditAmount(log.afterData, "amountCents");
    return `${orderNo ?? "订单"}${amountCents === null ? "" : ` · ${formatCny(amountCents)}`}`;
  }
  if (log.action === "PAYMENT_REVERSED") {
    const amountCents = auditAmount(log.afterData, "amountCents");
    const reason = auditText(log.afterData, "reason");
    const amount = amountCents === null ? "" : ` · ${formatCny(Math.abs(amountCents))}`;
    return `${orderNo ?? "订单"}${amount}${reason ? ` · ${reason}` : ""}`;
  }
  if (log.action === "INVENTORY_MOVEMENT_CREATED") {
    const documentNo = auditText(log.afterData, "documentNo") ?? log.entityId;
    const movementType = auditText(log.afterData, "movementType");
    const totalQuantity = auditAmount(log.afterData, "totalQuantity");
    const movementTypes: Record<string, string> = {
      PURCHASE_IN: "采购入库",
      SALES_OUT: "销售出库",
      ADJUSTMENT_IN: "盘点入库",
      ADJUSTMENT_OUT: "盘点出库",
    };
    return `${documentNo} · ${movementTypes[movementType ?? ""] ?? "库存进出"}${totalQuantity === null ? "" : ` · ${totalQuantity}`}`;
  }
  if (log.action === "INVENTORY_MOVEMENT_REVERSED") {
    const documentNo = auditText(log.afterData, "documentNo") ?? log.entityId;
    const reason = auditText(log.afterData, "reason");
    return `${documentNo}${reason ? ` · ${reason}` : ""}`;
  }
  if (log.action === "ACCOUNT_LOGIN") {
    return `账号 · ${auditText(log.afterData, "username") ?? log.actorName ?? log.entityId}`;
  }
  if (log.action === "PURCHASES_IMPORTED") {
    const documentCount = auditAmount(log.afterData, "importedDocumentCount");
    const rowCount = auditAmount(log.afterData, "importedRowCount");
    return documentCount === null || rowCount === null
      ? "进货批量导入"
      : `${documentCount} 张进货单 · ${rowCount} 行明细`;
  }
  if (log.action === "PURCHASE_IMPORT_REVALIDATED") {
    const rowCount = auditAmount(log.afterData, "rowCount");
    return rowCount === null ? "进货明细重新校验" : `${rowCount} 行进货明细重新校验`;
  }
  const entityLabels: Record<string, string> = {
    ACCOUNT: "账号",
    CATALOG: "商品目录",
    CUSTOMER: "客户",
    IMPORT_JOB: "导入任务",
    PRODUCT: "商品",
    SKU: "SKU",
    INVENTORY_DOCUMENT: "库存单据",
  };
  return `${entityLabels[log.entityType] ?? log.entityType} · ${log.entityId}`;
}

function AuditAdmin() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  useEffect(() => {
    void request<{ logs: AuditLog[] }>("/api/v1/admin/audit-logs")
      .then((result) => setLogs(result.logs))
      .catch((caught: unknown) => setError(loadErrorMessage(caught, "操作日志加载失败")))
      .finally(() => setLoading(false));
  }, [retryKey]);
  const labels: Record<string, string> = {
    ORDER_CREATED: "创建订单",
    ORDER_CREATED_ON_BEHALF: "代客下单",
    ORDER_CANCELLED: "取消订单",
    PAYMENT_RECORDED: "登记收款",
    PAYMENT_REVERSED: "冲销收款",
    SKU_PRICE_CHANGED: "修改价格",
    PASSWORD_RESET: "重置密码",
    PASSWORD_CHANGED: "修改密码",
    ACCOUNT_CREDENTIALS_UPDATED: "修改账号密码",
    CUSTOMER_PASSWORD_VIEWED: "查看客户密码",
    STAFF_PASSWORD_VIEWED: "查看内部账号密码",
    ACCOUNT_LOGIN: "账号登录",
    CUSTOMER_CREATED: "创建客户",
    CUSTOMER_UPDATED: "修改客户",
    CUSTOMER_EDITED: "编辑客户资料",
    PRODUCT_CREATED: "创建商品",
    PRODUCT_UPDATED: "修改商品",
    PRODUCT_IMAGE_UPDATED: "修改商品主图",
    PRODUCT_IMAGE_DELETED: "删除商品主图",
    SKU_CREATED: "创建 SKU",
    SKU_UPDATED: "更新 SKU",
    ORDER_FULFILLMENT_CHANGED: "推进订单流程",
    ORDER_REOPENED_AFTER_PAYMENT_REVERSAL: "回款冲销后重开订单",
    CUSTOMERS_IMPORTED: "导入客户",
    PRODUCTS_IMPORTED: "导入商品",
    PURCHASES_IMPORTED: "导入进货",
    PURCHASE_IMPORT_REVALIDATED: "校验进货修改",
    INVENTORY_MOVEMENT_CREATED: "登记库存单",
    INVENTORY_MOVEMENT_REVERSED: "冲销库存单",
  };
  if (loading) return <LoadingRows />;
  if (error) return <LoadError title="操作日志暂时无法加载" description={`${error}。请检查网络连接后重试。`} onRetry={() => { setLoading(true); setError(""); setRetryKey((value) => value + 1); }} />;
  return (
    <section className="admin-section flush">
      <div className="data-table audit-table">
        <div className="data-table__head">
          <span>时间</span>
          <span>操作人</span>
          <span>动作</span>
          <span>业务详情</span>
          <span>来源 IP</span>
        </div>
        {logs.map((log) => (
          <div className="data-table__row" key={log.id}>
            <span data-label="时间">{formatDateTime(log.createdAt)}</span>
            <span data-label="操作人"><b>{log.actorName || "系统"}</b></span>
            <span data-label="动作"><StatusBadge tone="info">
                {labels[log.action] ?? log.action}
              </StatusBadge></span>
            <span data-label="业务详情">{auditBusinessDetail(log)}</span>
            <span data-label="来源 IP">{log.ipAddress || "—"}</span>
          </div>
        ))}
      </div>
      {logs.length === 0 && (
        <EmptyState
          title="暂无操作日志"
          description="关键业务操作发生后会自动记录在这里。"
        />
      )}
    </section>
  );
}

function ShipmentModal({ order, inventoryEnabled, onClose, onDone, onPendingChange }: {
  order: Order;
  inventoryEnabled: boolean;
  onClose: () => void;
  onDone: (message: string, tone?: "success" | "info") => void;
  onPendingChange: (pending: boolean) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState("");
  const [inventoryPreview, setInventoryPreview] = useState<Record<string, { stockQuantity: number; quantityAfterShipment: number }>>({});
  const [previewLoading, setPreviewLoading] = useState(inventoryEnabled);
  const [previewError, setPreviewError] = useState("");
  const requestPending = useRef(false);
  const locked = loading || uncertain;
  useEffect(() => () => onPendingChange(false), [onPendingChange]);
  useEffect(() => {
    if (!inventoryEnabled) return;
    const controller = new AbortController();
    let active = true;
    void request<{ items: Array<{ itemId: string; stockQuantity: number; quantityAfterShipment: number }> }>(
      `/api/v1/admin/orders/${order.id}/inventory-preview`, { signal: controller.signal },
    ).then((result) => {
      if (active) setInventoryPreview(Object.fromEntries(result.items.map((item) => [item.itemId, item])));
    }).catch(() => {
      if (active) setPreviewError("暂时无法读取库存预估，仍可确认出货；系统将按实际库存扣减。");
    }).finally(() => { if (active) setPreviewLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [inventoryEnabled, order.id]);

  function successMessage(document?: { documentNo: string } | null) {
    return document
      ? `出货已完成，库存已扣减；销售出库单：${document.documentNo}`
      : "出货已登记";
  }

  async function checkResult() {
    const params = new URLSearchParams({ query: order.orderNo });
    const result = await request<{ orders: Order[] }>(`/api/v1/admin/orders?${params}`);
    const current = result.orders.find((row) => row.id === order.id);
    if (!current) throw new Error("未找到当前订单");
    if (current.fulfillmentStatus === "DISPATCHED" || current.fulfillmentStatus === "COMPLETED") {
      onPendingChange(false);
      onDone(successMessage(current.inventoryDocument));
    } else if (current.status === "CANCELLED") {
      onPendingChange(false);
      onDone("核对结果：订单已取消，请查看当前订单记录。", "info");
    } else {
      setUncertain(false);
      onPendingChange(false);
      setError("已核对当前订单，尚未出货。可重新确认出货。");
    }
  }

  async function submit() {
    if (requestPending.current) return;
    requestPending.current = true;
    setLoading(true);
    setError("");
    onPendingChange(true);
    try {
      if (uncertain) {
        await checkResult();
      } else {
        const result = await request<{ order?: { inventoryDocument?: { documentNo: string } | null }; inventoryDocument?: { documentNo: string } | null }>(
          `/api/v1/admin/orders/${order.id}/fulfillment`, {
            method: "PATCH", body: JSON.stringify({ status: "DISPATCHED" }),
          },
        );
        onPendingChange(false);
        onDone(successMessage(result.order?.inventoryDocument ?? result.inventoryDocument));
      }
    } catch (caught) {
      if (!uncertain && caught instanceof ApiError && caught.status >= 400 && caught.status < 500) {
        onPendingChange(false);
        setError(caught.message);
      } else {
        setUncertain(true);
        if (!uncertain) {
          try { await checkResult(); }
          catch { setError("出货结果尚未确认。请保持当前页面并重新核对结果，核对期间不会再次提交出货。"); }
        } else {
          setError("仍无法核对出货结果，请保持当前页面并重新核对；不会再次提交出货。");
        }
      }
    } finally {
      requestPending.current = false;
      setLoading(false);
    }
  }

  return (
    <Modal title="确认出货" description={`${order.orderNo} · ${order.customerName || order.customerSnapshot?.name || "客户"}`} onClose={locked ? () => undefined : onClose}
      footer={<><Button variant="secondary" disabled={locked} onClick={onClose}>取消</Button><Button loading={loading} onClick={() => void submit()}>{uncertain ? "重新核对出货结果" : "确认出货"}</Button></>}>
      <div className="order-operations__shipment">
        <p>{inventoryEnabled ? "确认后将按以下整单数量扣减库存，并生成销售出库单。库存不足也可出货，出货后的负库存将标红显示。" : "库存功能未启用，本次仅登记出货状态，不扣减库存。"}</p>
        {order.deliveryMethod === "SELF_PICKUP" && <p>请确认客户已提取以下商品。</p>}
        {error && <div className="inline-error" role="alert">{error}</div>}
        {previewLoading && <p role="status">正在读取库存…</p>}
        {previewError && <p role="status">{previewError}</p>}
        <ul className="order-operations__shipment-items">{order.items.map((item) => {
          const preview = inventoryPreview[item.id];
          return <li key={item.id}><span><strong>{item.productNameSnapshot}</strong><small>{item.skuCodeSnapshot}</small>
            {preview && <small>当前库存 <span className={preview.stockQuantity < 0 ? "stock-negative" : undefined}>{preview.stockQuantity}</span> {item.unitSnapshot} · 出货后 <b className={preview.quantityAfterShipment < 0 ? "stock-negative" : undefined}>{preview.quantityAfterShipment} {item.unitSnapshot}</b></small>}
          </span><b>{item.quantity} {item.unitSnapshot}</b></li>;
        })}</ul>
        {inventoryEnabled && !previewLoading && !previewError && <p className="order-operations__stock-note">库存为当前预估，确认出货时按最新库存计算。</p>}
      </div>
    </Modal>
  );
}

function PaymentModal({
  order,
  onClose,
  onDone,
  onPendingChange,
}: {
  order: Order;
  onClose: () => void;
  onDone: (completed: boolean) => void;
  onPendingChange: (pending: boolean) => void;
}) {
  const idempotencyKeyRef = useRef(crypto.randomUUID());
  const [form, setForm] = useState({
    amount: (order.outstandingCents! / 100).toFixed(2),
    paymentDate: todayInShanghai(),
    method: "银行转账",
    referenceNo: "",
    note: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uncertainSubmission, setUncertainSubmission] = useState<{
    idempotencyKey: string;
    amountCents: number;
    paymentDate: string;
    method: string;
    referenceNo: string;
    note: string;
  } | null>(null);
  const locked = loading || uncertainSubmission !== null;
  useEffect(() => () => onPendingChange(false), [onPendingChange]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    const amountCents = parseYuanToCents(form.amount);
    if (!uncertainSubmission && (amountCents === null || amountCents <= 0)) {
      setError("收款金额必须是最多两位小数的正数");
      setLoading(false);
      return;
    }
    const submission = uncertainSubmission ?? {
      idempotencyKey: idempotencyKeyRef.current,
      amountCents: amountCents!,
      paymentDate: form.paymentDate,
      method: form.method,
      referenceNo: form.referenceNo,
      note: form.note,
    };
    onPendingChange(true);
    try {
      const response = await request<{
        payment: { orderCompleted: boolean };
      }>(`/api/v1/admin/orders/${order.id}/payments`, {
        method: "POST",
        body: JSON.stringify(submission),
      });
      onPendingChange(false);
      onDone(response.payment.orderCompleted);
    } catch (caught) {
      if (!uncertainSubmission && caught instanceof ApiError && caught.status >= 400 && caught.status < 500) {
        setUncertainSubmission(null);
        onPendingChange(false);
        setError(caught.message);
      } else {
        setUncertainSubmission(submission);
        const reason = caught instanceof ApiError ? `${caught.message}。` : "";
        setError(`${reason}收款结果尚未确认，请保持当前页面并重试确认收款；重试同一笔收款不会重复记账。`);
      }
    } finally {
      setLoading(false);
    }
  }
  const update = (key: keyof typeof form, value: string) =>
    setForm((value0) => ({ ...value0, [key]: value }));
  const enteredAmountCents = parseYuanToCents(form.amount);
  const completesOrder = enteredAmountCents === order.outstandingCents;
  return (
    <Modal
      title="确认回款"
      description={`${order.orderNo} · ${order.customerName} · 未收 ${formatCny(order.outstandingCents!)}。全额回款提交后订单将自动完结，订单台账保留记录。`}
      onClose={locked ? () => undefined : onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={locked}>
            取消
          </Button>
          <Button type="submit" form="payment-form" loading={loading}>
            {uncertainSubmission ? "重试确认收款" : completesOrder ? "确认回款并完结" : "登记部分回款"}
          </Button>
        </>
      }
    >
      <form id="payment-form" className="form-grid" onSubmit={submit}>
        {error && <div className="inline-error form-grid__full" role="alert">{error}</div>}
        <Field label="本次收款金额（元）">
          <input
            inputMode="decimal"
            value={form.amount}
            disabled={locked}
            onChange={(e) => update("amount", e.target.value)}
            required
          />
        </Field>
        <Field label="收款日期">
          <input
            type="date"
            value={form.paymentDate}
            disabled={locked}
            onChange={(e) => update("paymentDate", e.target.value)}
            required
          />
        </Field>
        <Field label="收款方式">
          <select
            value={form.method}
            disabled={locked}
            onChange={(e) => update("method", e.target.value)}
          >
            <option>银行转账</option>
            <option>现金</option>
            <option>微信</option>
            <option>支付宝</option>
            <option>其他</option>
          </select>
        </Field>
        <Field label="凭证号">
          <input
            value={form.referenceNo}
            disabled={locked}
            onChange={(e) => update("referenceNo", e.target.value)}
            placeholder="选填"
          />
        </Field>
        <div className="form-grid__full">
          <Field label="备注">
            <textarea
              value={form.note}
              disabled={locked}
              onChange={(e) => update("note", e.target.value)}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}

function ReasonModal({
  title,
  description,
  label,
  action,
  danger,
  onClose,
  onSubmit,
}: {
  title: string;
  description: string;
  label: string;
  action: string;
  danger?: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await onSubmit(reason);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "操作失败");
    } finally {
      setLoading(false);
    }
  }
  return (
    <Modal
      title={title}
      description={description}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            返回
          </Button>
          <Button
            variant={danger ? "danger" : "primary"}
            form="reason-form"
            type="submit"
            loading={loading}
          >
            {action}
          </Button>
        </>
      }
    >
      <form id="reason-form" onSubmit={submit}>
        {error && <div className="inline-error" role="alert">{error}</div>}
        <Field label={label}>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            minLength={1}
            maxLength={300}
            required
            autoFocus
          />
        </Field>
      </form>
    </Modal>
  );
}

function CustomerModal({
  onClose,
  onCreated,
  canConfigurePermissions,
}: {
  onClose: () => void;
  onCreated: (name: string, password: string) => void;
  canConfigurePermissions: boolean;
}) {
  const [dealerPermissions, setDealerPermissions] = useState(() => getDealerPermissions({}));
  const [defaultAddressDetail, setDefaultAddressDetail] = useState("");
  const [form, setForm] = useState({
    code: "",
    name: "",
    username: "",
    contactName: "",
    phone: "",
    active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const update = (key: keyof typeof form, value: string | boolean) =>
    setForm((current) => ({ ...current, [key]: value }));
  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const detail = defaultAddressDetail.trim();
      const result = await request<{ temporaryPassword: string }>(
        "/api/v1/admin/customers",
        {
          method: "POST",
          body: JSON.stringify({
            ...form,
            ...(detail ? {
              defaultAddress: {
                label: "常用送货地址",
                contactName: form.contactName,
                phone: form.phone,
                detail,
                isDefault: true,
              },
            } : {}),
            ...(canConfigurePermissions ? { dealerPermissions } : {}),
          }),
        },
      );
      onCreated(form.name, result.temporaryPassword);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "客户创建失败");
    } finally {
      setLoading(false);
    }
  }
  return (
    <Modal
      title="新建经销商客户"
      description="系统将生成一次性初始密码，客户首次登录必须修改。"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
          <Button form="customer-form" type="submit" loading={loading}>
            创建客户账号
          </Button>
        </>
      }
    >
      <form id="customer-form" className="form-grid" onSubmit={submit}>
        {error && <div className="inline-error form-grid__full" role="alert">{error}</div>}
        <Field label="客户编码">
          <input
            value={form.code}
            onChange={(e) => update("code", e.target.value)}
            placeholder="例如 KH001"
            required
          />
        </Field>
        <Field label="客户名称">
          <input
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            required
          />
        </Field>
        <Field label="登录名">
          <input
            value={form.username}
            onChange={(e) => update("username", e.target.value)}
            placeholder="例如 dealer001"
            required
          />
        </Field>
        <Field label="联系人">
          <input
            value={form.contactName}
            onChange={(e) => update("contactName", e.target.value)}
            required
          />
        </Field>
        <Field label="联系电话">
          <input
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            required
          />
        </Field>
        <label className="check-field">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => update("active", e.target.checked)}
          />
          立即启用账号
        </label>
        <div className="form-grid__full">
          <Field label="常用送货地址" hint="选填；填写后设为默认送货地址，收货人和电话沿用上方联系人及联系电话。">
            <textarea
              value={defaultAddressDetail}
              onChange={(e) => setDefaultAddressDetail(e.target.value)}
              placeholder="请填写完整的省市区、街道及门牌号"
              rows={3}
              maxLength={300}
            />
          </Field>
        </div>
        {canConfigurePermissions && <>
          <PermissionPresets accountType="DEALER" value={dealerPermissions} onApply={setDealerPermissions} disabled={loading} />
          <DealerPermissionPicker value={dealerPermissions} onChange={setDealerPermissions} disabled={loading} />
        </>}
      </form>
    </Modal>
  );
}

function StaffModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (name: string, password: string) => void;
}) {
  const [staffPermissions, setStaffPermissions] = useState(() => getStaffPermissions({ staffRole: "VIEWER" }));
  const [form, setForm] = useState({
    username: "",
    displayName: "",
    staffRole: "VIEWER" as StaffAccount["staffRole"],
    active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const result = await request<{ temporaryPassword: string }>(
        "/api/v1/admin/staff",
        {
          method: "POST",
          body: JSON.stringify({ ...form, ...(form.staffRole === "CUSTOM" ? { staffPermissions } : {}) }),
        },
      );
      onCreated(form.displayName, result.temporaryPassword);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "内部账号创建失败",
      );
    } finally {
      setLoading(false);
    }
  }
  return (
    <Modal
      title="新建内部账号"
      description="选择自定义勾选，分别配置此账号可见的内容和允许的操作。"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
          <Button form="staff-form" type="submit" loading={loading}>
            创建内部账号
          </Button>
        </>
      }
    >
      <form id="staff-form" className="form-grid" onSubmit={submit}>
        {error && <div className="inline-error form-grid__full" role="alert">{error}</div>}
        <Field label="登录名">
          <input
            value={form.username}
            onChange={(event) =>
              setForm({ ...form, username: event.target.value })
            }
            placeholder="例如 sales01"
            required
          />
        </Field>
        <Field label="姓名">
          <input
            value={form.displayName}
            onChange={(event) =>
              setForm({ ...form, displayName: event.target.value })
            }
            required
          />
        </Field>
        <Field label="权限角色">
          <select
            value={form.staffRole}
            onChange={(event) =>
              setForm({
                ...form,
                staffRole: event.target.value as StaffAccount["staffRole"],
              })
            }
          >
            <option value="VIEWER">查看员</option>
            <option value="ADMIN">管理员</option>
            <option value="CUSTOM">自定义勾选</option>
          </select>
        </Field>
        <label className="check-field">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(event) =>
              setForm({ ...form, active: event.target.checked })
            }
          />
          立即启用账号
        </label>
        <PermissionPresets accountType="STAFF" value={form.staffRole === "CUSTOM" ? staffPermissions : getStaffPermissions({ staffRole: form.staffRole })} onApply={(value) => {
          setForm((previous) => ({ ...previous, staffRole: "CUSTOM" }));
          setStaffPermissions(value);
        }} disabled={loading} />
        {form.staffRole === "CUSTOM" && <StaffPermissionPicker value={staffPermissions} onChange={setStaffPermissions} disabled={loading} />}
      </form>
    </Modal>
  );
}

function ProductModal({
  categories,
  codeCategories,
  initialCategory,
  onClose,
  onDone,
  canManageCategories,
}: {
  categories: Category[];
  codeCategories: CodeCategoryNode[];
  initialCategory: string;
  onClose: () => void;
  onDone: () => void;
  canManageCategories: boolean;
}) {
  const initialSelection = initialCategory.startsWith("code:")
    ? (codeCategories.some((node) => `code:${node.prefix}` === initialCategory) ? initialCategory : "")
    : categories.some((category) => category.id === initialCategory && category.active !== false)
      ? initialCategory : codeCategories.length ? "" : categories.find((category) => category.active !== false)?.id ?? "";
  const [categoryValue, setCategoryValue] = useState(initialSelection);
  const categoryPrefix = categoryValue.startsWith("code:") ? categoryValue.slice(5) : "";
  const [form, setForm] = useState({
    code: initialSelection.startsWith("code:") ? initialSelection.slice(5) : "",
    name: "",
    description: "",
    active: true,
    unit: "",
    price: "",
    specification: "",
  });
  const [newCategory, setNewCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  function selectCategory(value: string) {
    const prefix = value.startsWith("code:") ? value.slice(5) : "";
    setCategoryValue(value);
    setNewCategory("");
    setError("");
    setForm((previous) => ({ ...previous,
      code: !previous.code.trim() || previous.code.trim() === categoryPrefix ? prefix : previous.code,
    }));
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    const { unit, price, specification, ...productForm } = form;
    const priceCents = parseYuanToCents(price);
    if (priceCents === null) return setError("含税单价必须是最多两位小数的非负金额");
    if (categoryPrefix && !matchesCategoryPrefix(form.code.trim(), categoryPrefix)) {
      return setError(`商品编码须以分类编码 ${categoryPrefix} 开头，并补齐商品序号。`);
    }
    setLoading(true);
    setError("");
    try {
      let categoryId = categoryPrefix ? "" : categoryValue;
      if (canManageCategories && newCategory.trim()) {
        const result = await request<{ category: Category }>(
          "/api/v1/admin/categories",
          {
            method: "POST",
            body: JSON.stringify({
              name: newCategory,
              sortOrder: 0,
              active: true,
            }),
          },
        );
        categoryId = result.category.id;
      }
      if (!categoryId && !categoryPrefix) throw new Error("请选择或新建分类");
      await request("/api/v1/admin/products", {
        method: "POST",
        body: JSON.stringify({ ...productForm, ...(categoryPrefix ? { categoryPrefix } : { categoryId }),
          initialSku: { specification, unit, priceCents } }),
      });
      onDone();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : caught instanceof Error
            ? caught.message
            : "商品创建失败",
      );
    } finally {
      setLoading(false);
    }
  }
  const legacyLabel = codeCategories.length ? "原分类名称" : "已有分类";
  const legacyCategoryFields = <>
    <div className="product-create-fields">
      <Field label={legacyLabel}>
        <select aria-label={legacyLabel} value={categoryPrefix ? "" : categoryValue}
          onChange={(event) => selectCategory(event.target.value)} disabled={loading}>
          <option value="">请选择</option>
          {categories.filter((category) => category.active !== false).map((category) => (
            <option key={category.id} value={category.id}>{category.name}</option>
          ))}
        </select>
      </Field>
      {canManageCategories && <Field label="或新建分类">
        <input value={newCategory} disabled={loading}
          onChange={(event) => {
            setNewCategory(event.target.value);
            if (categoryPrefix) setCategoryValue("");
          }} placeholder="选填，填写后使用新分类" />
      </Field>}
    </div>
    <p className="product-create-hint">数字商品的多级分类由商品编码自动识别。</p>
  </>;
  return (
    <Modal
      title="新建商品系列"
      description="填写名称、规格、单位和单价，一次保存。"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
          <Button form="product-form" type="submit" loading={loading}>
            创建商品
          </Button>
        </>
      }
    >
      <form id="product-form" className="product-create-form" onSubmit={submit}>
        {error && <div className="inline-error" role="alert">{error}</div>}
        <fieldset className="product-create-section">
          <legend><Folders aria-hidden="true" />分类设置</legend>
          {codeCategories.length > 0 && <>
            <CodeCategorySelect nodes={codeCategories} value={categoryValue} onChange={selectCategory}
              ariaLabel="已有分类" allLabel="请选择分类" purpose="create" disabled={loading} />
            <p className="product-create-hint">与左侧商品目录使用相同分类；选好分类后，请补齐商品编码。</p>
          </>}
          {codeCategories.length > 0 ? <details className="product-create-legacy"
            open={Boolean(categoryValue && !categoryPrefix) || Boolean(newCategory)}>
            <summary>使用原分类名称</summary>
            {legacyCategoryFields}
          </details> : legacyCategoryFields}
        </fieldset>
        <fieldset className="product-create-section">
          <legend><Package aria-hidden="true" />商品资料</legend>
          <div className="product-create-fields">
            <Field label="商品编码">
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="例如 010101"
                aria-describedby="product-create-code-hint"
                required
              />
            </Field>
          </div>
          <p id="product-create-code-hint" className="product-create-hint">编码用于识别商品，请保留前导零；已有编码请维护原档案。</p>
          <div className="product-create-fields product-create-fields--identity">
            <Field label="名称">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="填写商品名称"
                maxLength={160}
                required
              />
            </Field>
            <Field label="规格">
              <input
                value={form.specification}
                onChange={(e) => setForm({ ...form, specification: e.target.value })}
                placeholder="例如 尖头 15cm 或 Φ20 × 4m"
                maxLength={180}
                required
              />
            </Field>
          </div>
          {getProductCategoryPrefixes(form.code.trim()).length > 0
            && (!categoryPrefix || matchesCategoryPrefix(form.code.trim(), categoryPrefix))
            && <ProductCodeCategoryNote code={form.code} />}
        </fieldset>
        <fieldset className="product-create-section">
          <legend><Tag aria-hidden="true" />销售资料</legend>
          <div className="product-create-fields">
            <Field label="单位">
              <input
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                placeholder="例如 个、根、箱"
                maxLength={20}
                required
              />
            </Field>
            <Field label="单价">
              <input
                inputMode="decimal"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="例如 20.00"
                required
              />
            </Field>
          </div>
        </fieldset>
        <div className="product-create-description">
          <Field label="商品说明">
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              rows={3}
              placeholder="选填，可补充商品用途、材质或其他说明"
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}

function SkuModal({
  product,
  sku,
  completeProduct = false,
  onClose,
  onDone,
}: {
  product: Product;
  sku?: Sku;
  completeProduct?: boolean;
  onClose: () => void;
  onDone: (productCode?: string, action?: "created" | "renamed") => void;
}) {
  const categoryPrefix = getProductCategoryPrefixes(product.code.trim()).at(-1) ?? "";
  const syncProductCode = Boolean(sku && product.skus.length === 1 && categoryPrefix
    && getProductCategoryPrefixes(sku.code).at(-1) === categoryPrefix);
  const [standaloneProduct, setStandaloneProduct] = useState(!sku && !completeProduct && Boolean(categoryPrefix));
  const [productName, setProductName] = useState(product.name);
  const [form, setForm] = useState({
    code: sku?.code ?? (completeProduct ? product.code : categoryPrefix),
    specification: sku?.specification ?? "",
    unit: sku?.unit ?? (completeProduct ? "" : "根"),
    price: sku ? String(sku.priceCents / 100) : "",
    active: sku?.active ?? true,
    sortOrder: sku?.sortOrder ?? product.skus.length,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    const code = form.code.trim();
    if (standaloneProduct && (getProductCategoryPrefixes(code).at(-1) !== categoryPrefix || code === product.code)) {
      return setError(`同级商品编码须在分类编码 ${categoryPrefix} 后补齐两位商品序号，并与 ${product.code} 不同`);
    }
    if (standaloneProduct && !product.categoryId) return setError("请先设置原商品的分类名称分组");
    const priceCents = parseYuanToCents(form.price);
    if (priceCents === null)
      return setError("含税单价必须是最多两位小数的非负金额");
    setLoading(true);
    try {
      await request(
        sku ? `/api/v1/admin/skus/${sku.id}` : "/api/v1/admin/skus",
        {
          method: sku ? "PATCH" : "POST",
          body: JSON.stringify({
            ...(sku ? {} : { productId: product.id }),
            code,
            specification: form.specification,
            unit: form.unit,
            priceCents,
            active: form.active,
            sortOrder: form.sortOrder,
            ...(syncProductCode && !standaloneProduct ? { syncProductCode: true } : {}),
            ...(standaloneProduct ? { newProduct: {
              categoryId: product.categoryId, code, name: productName, description: "", active: true,
            } } : { productName }),
          }),
        },
      );
      onDone(standaloneProduct || syncProductCode ? code : undefined,
        standaloneProduct ? "created" : syncProductCode ? "renamed" : undefined);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : sku
            ? "SKU 更新失败"
            : "SKU 创建失败",
      );
    } finally {
      setLoading(false);
    }
  }
  return (
    <Modal
      title={standaloneProduct ? sku ? "设为独立商品" : "新建同级商品" : syncProductCode || completeProduct ? "编辑商品及规格" : sku ? "编辑 SKU 规格" : "新增规格 SKU"}
      description={product.name}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
          <Button form="sku-form" type="submit" loading={loading}>
            {standaloneProduct ? sku ? "保存为独立商品" : "创建商品" : sku || completeProduct ? "保存修改" : "创建 SKU"}
          </Button>
        </>
      }
    >
      <form id="sku-form" className="form-grid sku-edit-form" onSubmit={submit}>
        {error && <div className="inline-error form-grid__full" role="alert">{error}</div>}
        <div className="form-grid__full sku-category-note">
          {standaloneProduct ? <>
            <strong>与 {product.code} 同级 · 分类：{categoryPrefix.match(/.{2}/g)?.join(" / ")}</strong>
            <p>新编码单独显示，可独立维护商品名称、主图和规格。</p>
          </> : <>
            <ProductCodeCategoryNote code={syncProductCode ? form.code : product.code} /><strong>{categoryPrefix ? "分类名称分组" : "所属分类"}：{product.categoryName ?? "未分类"}</strong>
            <p>{completeProduct ? "补充当前商品的名称、规格、单位和单价，保存后显示在这条商品上。" : syncProductCode ? "修改商品编码会同步保存到商品和规格，目录只显示一个商品编码。" : categoryPrefix ? "多级分类由所属商品的完整商品编码识别。修改 SKU 编码或分类名称分组不会改变商品的编码分类路径。" : `SKU 跟随商品「${product.name}」的分类。需要更换时，可返回商品卡片点击“修改分类”，该商品的所有 SKU 会一起变更。`}</p>
          </>}
        </div>
        {categoryPrefix && !completeProduct && <label className="check-field form-grid__full">
          <input type="checkbox" checked={standaloneProduct} onChange={(event) => { setStandaloneProduct(event.target.checked); setError(""); }} />
          {sku ? "将此规格设为独立商品" : "新建同级商品"}（与 {product.code} 同级）
        </label>}
        <div className="form-grid__full"><Field label={standaloneProduct || syncProductCode || completeProduct ? "商品编码" : "SKU 编码"} hint={completeProduct
          ? "使用当前商品编码。"
          : syncProductCode && !standaloneProduct
          ? "保存后同步更新目录左侧的商品编码。"
          : !sku && categoryPrefix
          ? standaloneProduct ? `分类编码 ${categoryPrefix} 后填写两位商品序号。` : `已填入分类编码 ${categoryPrefix}，请补全规格编码。`
          : "每个规格使用唯一编码。下单和库存以所选 SKU 为准，名称和规格文字用于核对。"}>
          <input
            value={form.code}
            readOnly={completeProduct}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            required
          />
        </Field></div>
        <Field label="名称">
          <input value={productName} onChange={(event) => setProductName(event.target.value)} required maxLength={160} />
        </Field>
        <Field label="规格">
          <input
            value={form.specification}
            onChange={(e) =>
              setForm({ ...form, specification: e.target.value })
            }
            placeholder="例如 Φ20 × 3.4mm × 4m"
            required
          />
        </Field>
        <Field label="单位">
          <input
            value={form.unit}
            onChange={(e) => setForm({ ...form, unit: e.target.value })}
            required
          />
        </Field>
        <Field label="单价">
          <input
            inputMode="decimal"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            required
          />
        </Field>
        <Field label="显示顺序" hint="数字越小越靠前">
          <input
            type="number"
            min={0}
            max={99_999}
            value={form.sortOrder}
            onChange={(event) =>
              setForm({ ...form, sortOrder: Number(event.target.value) })
            }
            required
          />
        </Field>
        <label className="check-field">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(event) =>
              setForm({ ...form, active: event.target.checked })
            }
          />
          保存后立即上架
        </label>
      </form>
    </Modal>
  );
}

function LoadingRows() {
  return (
    <div className="loading-block" role="status" aria-label="正在加载数据">
      <span className="skeleton" />
      <span className="skeleton" />
      <span className="skeleton" />
    </div>
  );
}
